import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import {
  getAllMobileNos,
  metaMessageSend,
  metaMessageTemplates,
} from "../../../api/Messages";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Master from "../Master";
import { batchList } from "../../../api/Batch";
import { courseList } from "../../../api/Courses";
import { GetMarketingSource } from "../../../api/MarketinService";
import { busList, busStudentsList } from "../../../api/Transport";
import { StaffList } from "../../../api/Staff";

const AUDIENCE_TYPES = [
  // "All Students",
  "Marketing Students",
  "Batch Students",
  "Enquiry Students",
  "Bus Students",
  "Staffs",
];

const BATCH_SIZE = 20;
const BATCH_DELAY_MS = 1000;
const MOBILE_PATTERN = /^\d{10}$/;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const findComponent = (template, type) =>
  template?.components?.find((c) => c.type === type);

const parseMobiles = (raw, keepDuplicates = false) => {
  const entries = String(raw || "")
    .split(/[\s,;]+/)
    .map((m) => m.trim())
    .filter(Boolean);

  const valid = [];
  const invalid = [];

  entries.forEach((entry) => {
    if (MOBILE_PATTERN.test(entry)) {
      if (keepDuplicates || !valid.includes(entry)) {
        valid.push(entry);
      }
    } else if (!invalid.includes(entry)) {
      invalid.push(entry);
    }
  });

  return {
    valid,
    invalid,
  };
};

/* =========================================================
   STAFF HELPERS

   Staff API row se sirf safe aur useful fields lete hain.
   password / remember_token / otp jaise sensitive fields
   kabhi recipient object me nahi aate, isliye wo dynamic
   variable dropdown me bhi show nahi honge.
========================================================= */

const pickStaffRows = (payload) => {
  const candidates = [
    payload?.data?.staff,
    payload?.staff,
    payload?.data?.data,
    payload?.data,
    payload,
  ];

  return candidates.find(Array.isArray) ?? [];
};

const getStaffRoleName = (staff) => {
  try {
    const role =
      typeof staff?.app_role === "string"
        ? JSON.parse(staff.app_role)
        : staff?.app_role;

    return role?.name ?? "";
  } catch {
    return "";
  }
};

const normalizeStaff = (staff) => {
  // mobile number ho sakta hai (8107274713) ya string ("+91 81072 74713").
  // Sirf digits rakho, last 10 lo.
  const mobile = String(staff?.mobile ?? "")
    .replace(/\D/g, "")
    .slice(-10);

  return {
    id: staff.id,
    staff_id: staff.id,
    registration_no: staff.registration_no ?? "",
    staff_type: staff.staff_type ?? "",
    role_name: getStaffRoleName(staff),
    name: staff.name ?? "",
    father_name: staff.father_name ?? "",
    email: staff.email ?? "",
    gender: staff.gender ?? "",
    dob: staff.dob ?? "",
    village: staff.village ?? "",
    tehsil: staff.tehsil ?? "",
    district: staff.district ?? "",
    state: staff.state ?? "",
    pincode: staff.pincode ?? "",
    last_education: staff.last_education ?? "",
    last_school: staff.last_school ?? "",
    status: staff.status ?? "",
    mobile,
    phone: mobile,
    is_staff: true,
  };
};

/* =========================================================
   FLATTEN OBJECT
   Recipient ke andar jo bhi data hai usko readable
   dynamic fields me convert karega.
========================================================= */

const flattenObject = (obj, prefix = "") => {
  const result = {};

  if (!obj || typeof obj !== "object") {
    return result;
  }

  Object.entries(obj).forEach(([key, value]) => {
    const fieldName = prefix ? `${prefix}.${key}` : key;

    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      Object.assign(result, flattenObject(value, fieldName));
    } else {
      result[fieldName] = value;
    }
  });

  return result;
};

/* =========================================================
   GET DYNAMIC VALUE
========================================================= */

const getDynamicValue = (recipient, field) => {
  if (!recipient || !field) {
    return "";
  }

  const flat = flattenObject(recipient);

  let value = flat[field];

  if (
    value === undefined &&
    Object.prototype.hasOwnProperty.call(recipient, field)
  ) {
    value = recipient[field];
  }

  /* Batch ID array */
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  if (value === null || value === undefined) {
    return "";
  }

  return String(value);
};

/* =========================================================
   TEMPLATE EXAMPLE VARIABLES
========================================================= */

const toVariables = (example) =>
  Array.isArray(example)
    ? example.flat().map((value) => ({
        value: String(value ?? ""),
        mode: "fixed",
        dynamicField: "",
      }))
    : [];

/* =========================================================
   BUTTON VARIABLES
========================================================= */

const toButtonVariables = (template) => {
  const buttons = findComponent(template, "BUTTONS")?.buttons ?? [];

  return buttons.flatMap((btn, btnIndex) =>
    (btn.url?.match(/{{(\d+)}}/g) ?? []).map((token) => ({
      index: String(btnIndex),
      placeholder: token.replace(/[{}]/g, ""),
      value: "",
      mode: "fixed",
      dynamicField: "",
      buttonType: btn.type,
      buttonText: btn.text,
    })),
  );
};

/* =========================================================
   DYNAMIC FIELD LABEL
========================================================= */

const formatDynamicLabel = (key) => {
  if (!key) return "";

  return key
    .split(".")
    .map((part) =>
      part.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase()),
    )
    .join(" → ");
};

const VariableInput = ({
  label,
  variable,
  index,
  setter,
  dynamicFields,
  updateVariable,
  formatDynamicLabel,
  entityLabel = "student",
}) => {
  const isDynamic = variable?.mode === "dynamic";
  const entityTitle =
    entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1);

  return (
    <div className="col-12 mb-3">
      <div className="border rounded p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <strong>{label}</strong>

          <span
            className={`badge ${
              isDynamic ? "bg-label-primary" : "bg-label-success"
            }`}
          >
            {isDynamic ? "Dynamic" : "Fixed"}
          </span>
        </div>

        <div className="row">
          {/* VALUE TYPE */}
          <div className="col-md-4 mb-2">
            <div className="form-floating form-floating-outline">
              <select
                className="form-select"
                value={variable?.mode || "fixed"}
                onChange={(e) => {
                  const mode = e.target.value;

                  updateVariable(setter, index, {
                    mode,
                    dynamicField:
                      mode === "dynamic" ? variable?.dynamicField || "" : "",
                  });
                }}
              >
                <option value="fixed">Enter a fixed value</option>

                <option value="dynamic" disabled={dynamicFields.length === 0}>
                  Use {entityLabel} data
                </option>
              </select>

              <label>How should this value be filled?</label>
            </div>
          </div>

          {/* FIXED VALUE */}
          {!isDynamic && (
            <div className="col-md-8 mb-2">
              <div className="form-floating form-floating-outline">
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Enter value for ${label}`}
                  value={variable?.value || ""}
                  onChange={(e) => {
                    const value = e.target.value;

                    updateVariable(setter, index, { value });
                  }}
                />

                <label>Fixed value</label>
              </div>
            </div>
          )}

          {/* DYNAMIC DATA */}
          {isDynamic && (
            <div className="col-md-8 mb-2">
              <div className="form-floating form-floating-outline">
                <select
                  className="form-select"
                  value={variable?.dynamicField || ""}
                  onChange={(e) => {
                    updateVariable(setter, index, {
                      dynamicField: e.target.value,
                    });
                  }}
                >
                  <option value="">Choose {entityLabel} field</option>

                  {dynamicFields.map((field) => (
                    <option key={field} value={field}>
                      {formatDynamicLabel(field)}
                    </option>
                  ))}
                </select>

                <label>{entityTitle} field</label>
              </div>
            </div>
          )}
        </div>

        {/* SELECTED DYNAMIC FIELD */}
        {isDynamic && variable?.dynamicField && (
          <div className="small text-primary">
            Uses {entityLabel} field:
            <strong className="ms-1">
              {formatDynamicLabel(variable.dynamicField)}
            </strong>
          </div>
        )}
      </div>
    </div>
  );
};

export default function MetaWaSendMsgV2() {
  /* =========================================================
       BASIC
    ========================================================= */

  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const [filters, setFilters] = useState({
    type: "",
    session_id: "",
  });

  const [sessions, setSessions] = useState([]);

  /* =========================================================
       FILTER DATA
    ========================================================= */

  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sources, setSources] = useState([]);

  const [selectedBatches, setSelectedBatches] = useState([]);

  const [selectedCourses, setSelectedCourses] = useState([]);

  const [selectedSources, setSelectedSources] = useState([]);

  const [buses, setBuses] = useState([]);
  const [selectedBusId, setSelectedBusId] = useState("");

  /* =========================================================
       RECIPIENTS
    ========================================================= */

  const [mobileNos, setMobileNos] = useState("");
  const [recipients, setRecipients] = useState([]);

  /* =========================================================
       TEMPLATES
    ========================================================= */

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [headerFile, setHeaderFile] = useState(null);

  const [headerPreview, setHeaderPreview] = useState("");

  const [headerVariables, setHeaderVariables] = useState([]);

  const [bodyVariables, setBodyVariables] = useState([]);

  const [buttonVariables, setButtonVariables] = useState([]);

  /* =========================================================
       LIVE PREVIEW STUDENT
       User can switch the sample recipient from the preview panel.
    ========================================================= */
  const [previewRecipientIndex, setPreviewRecipientIndex] = useState(0);

  // null = all students selected by default, [] = none selected.
  const [selectedRecipientKeys, setSelectedRecipientKeys] = useState(null);
  const [studentSearch, setStudentSearch] = useState("");
  const [showOnlySelected, setShowOnlySelected] = useState(false);

  // Modal states
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showVariableModal, setShowVariableModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [studentFeeFilter, setStudentFeeFilter] = useState("all");
  const [studentRteFilter, setStudentRteFilter] = useState("all");
  const [studentTypeFilter, setStudentTypeFilter] = useState("all");

  /* =========================================================
       PROGRESS
    ========================================================= */

  const [progress, setProgress] = useState({
    total: 0,
    sent: 0,
    failed: 0,
    pending: 0,
  });

  const [failures, setFailures] = useState([]);

  const stopRef = useRef(false);

  // Frontend-only sending protection.
  // IMPORTANT: a browser refresh/close destroys JavaScript, so this cannot
  // resume the actual API loop after refresh. We persist the last progress
  // only so the next page load can tell the user that the previous sending
  // session was interrupted.
  const SEND_STORAGE_KEY = "meta_whatsapp_frontend_send_session";
  const allowLeaveRef = useRef(false);
  const navigationGuardRef = useRef(false);
  const sendSessionRef = useRef(null);

  /* =========================================================
       TYPES
    ========================================================= */

  // const isAllType =
  //   filters.type === "All Students";

  const isMarketingType = filters.type === "Marketing Students";

  const isBatchType = filters.type === "Batch Students";

  const isEnquiryType = filters.type === "Enquiry Students";

  const isBusType = filters.type === "Bus Students";

  const isStaffType = filters.type === "Staffs";

  // Wording: staff audience me "student" ki jagah "staff" dikhega.
  const entity = isStaffType
    ? {
        one: "staff member",
        many: "staff",
        One: "Staff member",
        Many: "Staff",
        short: "staff",
      }
    : {
        one: "student",
        many: "students",
        One: "Student",
        Many: "Students",
        short: "student",
      };

  /* =========================================================
       MOBILE
    ========================================================= */

  const { valid: validMobiles, invalid: invalidMobiles } = useMemo(
    () => parseMobiles(mobileNos, Boolean(filters.type)),
    [mobileNos, filters.type],
  );

  /* =========================================================
       HEADER
    ========================================================= */

  const headerFormat = findComponent(selectedTemplate, "HEADER")?.format;

  const needsHeaderFile = ["IMAGE", "DOCUMENT", "VIDEO"].includes(headerFormat);

  /* =========================================================
       ERROR
    ========================================================= */

  const showError = useCallback((error, fallback = "Something went wrong") => {
    SweetAlertShow(
      "error",
      "Error",
      error?.response?.data?.message ?? error?.message ?? fallback,
    );
  }, []);

  /* =========================================================
       SEND SESSION / REFRESH / LEAVE PROTECTION

       Frontend-only limitation:
       - API calls continue while this page is alive.
       - Refresh/close destroys the JS process, so sending cannot continue
         after refresh without a backend queue.
       - We warn before refresh/close and before normal same-origin link
         navigation.
    ========================================================= */

  useEffect(() => {
    // If the browser was refreshed/closed while sending, the previous
    // session is still stored here. It cannot be resumed frontend-only.
    try {
      const raw = localStorage.getItem(SEND_STORAGE_KEY);

      if (raw) {
        const previous = JSON.parse(raw);

        if (previous?.status === "sending") {
          setTimeout(() => {
            SweetAlertShow(
              "warning",
              "Previous sending was interrupted",
              `The previous message sending was active when this page was refreshed or closed.\n\nSent: ${previous.sent ?? 0}\nFailed: ${previous.failed ?? 0}\nPending: ${previous.pending ?? 0}\n\nBecause this is frontend-only sending, it cannot continue after the page is destroyed. Please start sending again if required.`,
            );
          }, 300);
        }

        localStorage.removeItem(SEND_STORAGE_KEY);
      }
    } catch {
      localStorage.removeItem(SEND_STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!sending || allowLeaveRef.current) return;

      try {
        localStorage.setItem(
          SEND_STORAGE_KEY,
          JSON.stringify({
            ...(sendSessionRef.current || {}),
            status: "sending",
            sent: progress.sent,
            failed: progress.failed,
            pending: progress.pending,
            total: progress.total,
            interruptedAt: new Date().toISOString(),
          }),
        );
      } catch {}

      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [sending, progress]);

  useEffect(() => {
    if (!sending) return undefined;

    // Add a history guard so browser Back also asks before leaving.
    const guardUrl = window.location.href;
    const guardState = {
      ...(window.history.state || {}),
      __metaWhatsAppSendingGuard: true,
    };

    try {
      window.history.pushState(guardState, "", guardUrl);
    } catch {}

    const handlePopState = async () => {
      if (allowLeaveRef.current || navigationGuardRef.current) return;

      navigationGuardRef.current = true;

      try {
        const result = await window.Swal?.fire?.({
          icon: "warning",
          title: "Messages are still sending",
          html: `
                        <div class="text-start">
                            <p class="mb-2">Messages are still being sent.</p>
                            <div class="alert alert-warning py-2 mb-2">
                                Going back will stop browser-based sending.
                            </div>
                            <div class="small text-muted">
                                Sent: <strong>${progress.sent}</strong> ·
                                Failed: <strong>${progress.failed}</strong> ·
                                Pending: <strong>${progress.pending}</strong>
                            </div>
                        </div>
                    `,
          showCancelButton: true,
          confirmButtonText: "Go Back & Stop",
          cancelButtonText: "Stay & Continue",
          reverseButtons: true,
          allowOutsideClick: false,
        });

        if (result?.isConfirmed) {
          allowLeaveRef.current = true;
          stopRef.current = true;
          try {
            localStorage.removeItem(SEND_STORAGE_KEY);
          } catch {}
          window.history.back();
        } else {
          try {
            window.history.pushState(guardState, "", guardUrl);
          } catch {}
        }
      } finally {
        navigationGuardRef.current = false;
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [sending, progress]);

  useEffect(() => {
    if (!sending) return undefined;

    const handleDocumentClick = async (event) => {
      if (allowLeaveRef.current || navigationGuardRef.current) return;

      const anchor = event.target?.closest?.("a[href]");

      if (!anchor) return;
      if (anchor.target === "_blank") return;
      if (anchor.hasAttribute("download")) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("javascript:")) {
        return;
      }

      let url;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (url.href === window.location.href) return;

      event.preventDefault();
      event.stopPropagation();
      navigationGuardRef.current = true;

      try {
        const result = await window.Swal?.fire?.({
          icon: "warning",
          title: "Messages are still sending",
          html: `
                        <div class="text-start">
                            <p class="mb-2">Messages are currently being sent from this page.</p>
                            <div class="alert alert-warning py-2 mb-2">
                                If you leave this page, browser-based sending will stop.
                            </div>
                            <div class="small text-muted">
                                Sent: <strong>${progress.sent}</strong> ·
                                Failed: <strong>${progress.failed}</strong> ·
                                Pending: <strong>${progress.pending}</strong>
                            </div>
                        </div>
                    `,
          showCancelButton: true,
          confirmButtonText: "Leave & Stop Sending",
          cancelButtonText: "Stay & Continue Sending",
          reverseButtons: true,
          allowOutsideClick: false,
        });

        if (result?.isConfirmed) {
          allowLeaveRef.current = true;
          stopRef.current = true;
          try {
            localStorage.removeItem(SEND_STORAGE_KEY);
          } catch {}
          window.location.href = url.href;
        }
      } finally {
        navigationGuardRef.current = false;
      }
    };

    document.addEventListener("click", handleDocumentClick, true);

    return () => {
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [sending, progress]);

  /* =========================================================
       INITIAL LOAD
    ========================================================= */

  useEffect(() => {
    let cancelled = false;

    const loadInitialData = async () => {
      setLoading(true);

      try {
        const templateResponse = await metaMessageTemplates();

        if (!cancelled) {
          const list = templateResponse?.data?.data ?? [];

          setTemplates(
            list.filter((template) => template.status === "APPROVED"),
          );
        }

        /* =================================================
                   SESSIONS
                ================================================= */

        let storedSessions = [];

        try {
          storedSessions = JSON.parse(localStorage.getItem("sessions") ?? "[]");
        } catch {
          storedSessions = [];
        }

        if (!cancelled) {
          setSessions(storedSessions);

          /* Active session default */

          const activeSession = storedSessions.find(
            (session) => Number(session.status) === 1,
          );

          if (activeSession) {
            setFilters((prev) => ({
              ...prev,
              session_id: String(activeSession.id),
            }));
          }
        }
      } catch (error) {
        if (!cancelled) {
          showError(error, "Initial data could not be loaded");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadInitialData();

    return () => {
      cancelled = true;
    };
  }, [showError]);

  /* =========================================================
       ACTIVE SESSION DEFAULT WHEN AUDIENCE SELECTED
       
       IMPORTANT:
       Audience change par active session automatically
       select hoga.
    ========================================================= */

  useEffect(() => {
    if (!filters.type) {
      return;
    }

    const activeSession = sessions.find(
      (session) => Number(session.status) === 1,
    );

    if (activeSession && !filters.session_id) {
      setFilters((prev) => ({
        ...prev,
        session_id: String(activeSession.id),
      }));
    }
  }, [filters.type, filters.session_id, sessions]);

  /* =========================================================
       LOAD BATCHES
    ========================================================= */

  useEffect(() => {
    if (!isBatchType || !filters.session_id) {
      setBatches([]);
      setSelectedBatches([]);
      return;
    }

    let cancelled = false;

    const loadBatches = async () => {
      setLoading(true);

      try {
        const response = await batchList(
          `&session_id=${encodeURIComponent(filters.session_id)}`,
        );

        if (cancelled) return;

        if (!response?.data?.status) {
          setBatches([]);

          SweetAlertShow(
            "info",
            "Warning",
            response?.data?.message ?? "Batches not found",
          );

          return;
        }

        setBatches(response?.data?.data ?? []);
      } catch (error) {
        if (cancelled) return;

        setBatches([]);

        showError(error, "Batches could not be loaded");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setSelectedBatches([]);

    loadBatches();

    return () => {
      cancelled = true;
    };
  }, [isBatchType, filters.session_id, showError]);

  /* =========================================================
       LOAD COURSES
       
       ONLY ENQUIRY
    ========================================================= */

  useEffect(() => {
    if (!isEnquiryType || !filters.session_id) {
      setCourses([]);
      setSelectedCourses([]);
      return;
    }

    let cancelled = false;

    const loadCourses = async () => {
      setLoading(true);

      try {
        const response = await courseList(
          `&session_id=${encodeURIComponent(filters.session_id)}`,
        );

        if (cancelled) return;

        if (!response?.data?.status) {
          setCourses([]);

          SweetAlertShow(
            "info",
            "Warning",
            response?.data?.message ?? "Courses not found",
          );

          return;
        }

        setCourses(response?.data?.data ?? []);
      } catch (error) {
        if (cancelled) return;

        setCourses([]);

        showError(error, "Courses could not be loaded");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setSelectedCourses([]);

    loadCourses();

    return () => {
      cancelled = true;
    };
  }, [isEnquiryType, filters.session_id, showError]);

  /* =========================================================
       LOAD MARKETING SOURCE
       
       MARKETING = SOURCE ONLY
    ========================================================= */

  useEffect(() => {
    if (!isMarketingType) {
      setSources([]);
      setSelectedSources([]);
      return;
    }

    let cancelled = false;

    const loadSources = async () => {
      setLoading(true);

      try {
        const response = await GetMarketingSource();

        if (cancelled) return;

        if (!response?.data?.status) {
          setSources([]);

          SweetAlertShow(
            "info",
            "Warning",
            response?.data?.message ?? "Marketing sources not found",
          );

          return;
        }

        setSources(response?.data?.data ?? []);
      } catch (error) {
        if (cancelled) return;

        setSources([]);

        showError(error, "Marketing sources could not be loaded");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setSelectedSources([]);

    loadSources();

    return () => {
      cancelled = true;
    };
  }, [isMarketingType, showError]);

  /* =========================================================
   LOAD BUSES
========================================================= */

  useEffect(() => {
    if (!isBusType) {
      setBuses([]);
      setSelectedBusId("");
      return;
    }

    let cancelled = false;

    const loadBuses = async () => {
      setLoading(true);

      try {
        const response = await busList(
          filters.session_id
            ? `&session_id=${encodeURIComponent(filters.session_id)}`
            : "",
        );

        if (cancelled) return;

        if (!response?.data?.status) {
          setBuses([]);

          SweetAlertShow(
            "info",
            "Warning",
            response?.data?.message ?? "Buses not found",
          );

          return;
        }

        setBuses(response?.data?.data ?? []);
      } catch (error) {
        if (cancelled) return;

        setBuses([]);
        showError(error, "Buses could not be loaded");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    setSelectedBusId("");
    setMobileNos("");
    setRecipients([]);

    loadBuses();

    return () => {
      cancelled = true;
    };
  }, [isBusType, filters.session_id, showError]);

  /* =========================================================
       LOAD RECIPIENTS
    ========================================================= */

  useEffect(() => {
    if (!filters.type) {
      setMobileNos("");
      setRecipients([]);
      return;
    }

    /* BATCH */

    if (isBatchType && (!filters.session_id || !selectedBatches.length)) {
      setMobileNos("");
      setRecipients([]);
      return;
    }

    /* ENQUIRY */

    if (isEnquiryType && (!filters.session_id || !selectedCourses.length)) {
      setMobileNos("");
      setRecipients([]);
      return;
    }

    /* MARKETING */

    if (isMarketingType && (!filters.session_id || !selectedSources.length)) {
      setMobileNos("");
      setRecipients([]);
      return;
    }

    /* BUS STUDENTS */

    if (isBusType && (!filters.session_id || !selectedBusId)) {
      setMobileNos("");
      setRecipients([]);
      return;
    }

    let cancelled = false;

    const loadMobiles = async () => {
      setLoading(true);

      try {
        let query = `&type=${encodeURIComponent(filters.type)}`;

        /* =================================================
                   SESSION
                ================================================= */

        if (isBatchType || isEnquiryType || isMarketingType) {
          query += `&session_id=${encodeURIComponent(filters.session_id)}`;
        }

        /* =================================================
                   BATCH
                ================================================= */

        if (isBatchType) {
          selectedBatches.forEach((id) => {
            query += `&batch_ids[]=${encodeURIComponent(id)}`;
          });
        }

        /* =================================================
                   ENQUIRY COURSE
                ================================================= */

        if (isEnquiryType) {
          selectedCourses.forEach((id) => {
            query += `&course_ids[]=${encodeURIComponent(id)}`;
          });
        }

        /* =================================================
                   MARKETING SOURCE
                ================================================= */

        if (isMarketingType) {
          selectedSources.forEach((id) => {
            query += `&source_ids[]=${encodeURIComponent(id)}`;
          });
        }

        /* =================================================
                   STAFF
                   Session / batch / course ki zaroorat nahi.
                   Sirf wahi staff aayenge jinke paas valid mobile hai.
                ================================================= */

        if (isStaffType) {
          const response = await StaffList("&limit=all");

          if (cancelled) return;

          if (response?.data?.status === false) {
            setMobileNos("");
            setRecipients([]);

            SweetAlertShow(
              "info",
              "Warning",
              response?.data?.message ?? "Staff not found",
            );

            return;
          }

          const normalizedStaff = pickStaffRows(response?.data)
            .map(normalizeStaff)
            .filter((staff) => MOBILE_PATTERN.test(staff.mobile));

          if (!normalizedStaff.length) {
            setMobileNos("");
            setRecipients([]);

            SweetAlertShow(
              "info",
              "Warning",
              "No staff with a valid mobile number found",
            );

            return;
          }

          setRecipients(normalizedStaff);
          setMobileNos(normalizedStaff.map((staff) => staff.mobile).join(", "));

          return;
        }

        /* =================================================
   BUS STUDENTS
================================================= */

        if (isBusType) {
          try {
            const query = [
              `bus_id=${encodeURIComponent(selectedBusId)}`,
              `session_id=${encodeURIComponent(filters.session_id)}`,
              "limit=all",
            ].join("&");

            const response = await busStudentsList(query);

            if (cancelled) return;

            if (!response?.data?.status) {
              setMobileNos("");
              setRecipients([]);

              SweetAlertShow(
                "info",
                "Warning",
                response?.data?.message ?? "Bus students not found",
              );

              return;
            }

            const rows = response?.data?.data ?? [];

            // Convert BusStudent API records into the recipient structure
            // already used by WhatsApp preview, selection and sending.
            const normalizedRecipients = rows
              .filter((row) => row?.student)
              .map((row) => {
                const student = row.student;

                return {
                  ...row,
                  ...student,

                  // Preserve the actual student ID for selection and messaging.
                  id: student.id,
                  student_id: student.id,
                  bus_student_id: row.id,

                  name: student.name ?? "",
                  registration_no: student.registration_no ?? "",
                  father_name: student.father_name ?? "",
                  mother_name: student.mother_name ?? "",

                  mobile: student.phone ?? "",
                  phone: student.phone ?? "",

                  is_bus: true,
                  bus_student: "Yes",

                  // Retain nested data for dynamic template variables.
                  student,
                  bus: row.bus ?? null,
                  stop: row.stop ?? null,
                };
              });

            setRecipients(normalizedRecipients);

            setMobileNos(
              normalizedRecipients
                .map((student) => student.mobile)
                .filter(Boolean)
                .join(", "),
            );

            return;
          } catch (error) {
            if (cancelled) return;

            setMobileNos("");
            setRecipients([]);

            showError(error, "Bus students could not be loaded");
            return;
          } finally {
            if (!cancelled) {
              setLoading(false);
            }
          }
        }

        const response = await getAllMobileNos(query);

        if (cancelled) return;

        if (!response?.data?.status) {
          setMobileNos("");
          setRecipients([]);

          SweetAlertShow(
            "info",
            "Warning",
            response?.data?.message ?? "Recipients not found",
          );

          return;
        }

        const data = response?.data?.data ?? {};

        setMobileNos((data.mobile_nos ?? []).join(", "));

        setRecipients(data.recipients ?? []);
      } catch (error) {
        if (cancelled) return;

        setMobileNos("");
        setRecipients([]);

        showError(
          error,
          isStaffType
            ? "Staff could not be loaded"
            : "Mobile numbers could not be loaded",
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadMobiles();

    return () => {
      cancelled = true;
    };
  }, [
    filters.type,
    filters.session_id,
    selectedBusId,
    selectedBatches,
    selectedCourses,
    selectedSources,
    isBatchType,
    isEnquiryType,
    isMarketingType,
    isBusType,
    isStaffType,
    showError,
  ]);

  /* =========================================================
       DYNAMIC FIELDS
       
       Recipients ke actual response se fields banenge.
    ========================================================= */

  /* =========================================================
       STUDENT / FEE / FACILITY FILTER HELPERS
    ========================================================= */

  const numberValue = (value) => {
    const parsed = Number.parseFloat(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  // Current selected session total. Prefer grand_due because it includes
  // batch + bus + hostel. Fall back safely for older API responses.
  const getDueAmount = (recipient) => {
    if (
      recipient?.grand_due !== undefined &&
      recipient?.grand_due !== null &&
      recipient?.grand_due !== ""
    ) {
      return numberValue(recipient.grand_due);
    }

    if (
      recipient?.batch_total_due !== undefined ||
      recipient?.bus_due !== undefined ||
      recipient?.hostel_due !== undefined
    ) {
      return (
        numberValue(recipient?.batch_total_due) +
        numberValue(recipient?.bus_due) +
        numberValue(recipient?.hostel_due)
      );
    }

    return numberValue(recipient?.total_due ?? recipient?.all_session_due ?? 0);
  };

  const getPaidAmount = (recipient) => {
    if (
      recipient?.grand_paid !== undefined &&
      recipient?.grand_paid !== null &&
      recipient?.grand_paid !== ""
    ) {
      return numberValue(recipient.grand_paid);
    }

    if (
      recipient?.batch_total_paid !== undefined ||
      recipient?.bus_paid !== undefined ||
      recipient?.hostel_paid !== undefined
    ) {
      return (
        numberValue(recipient?.batch_total_paid) +
        numberValue(recipient?.bus_paid) +
        numberValue(recipient?.hostel_paid)
      );
    }

    return numberValue(
      recipient?.total_paid ?? recipient?.all_session_paid ?? 0,
    );
  };

  const getFinalFeeAmount = (recipient) => {
    if (
      recipient?.grand_final_fee !== undefined &&
      recipient?.grand_final_fee !== null &&
      recipient?.grand_final_fee !== ""
    ) {
      return numberValue(recipient.grand_final_fee);
    }

    if (
      recipient?.batch_total_fee !== undefined ||
      recipient?.bus_final_fee !== undefined ||
      recipient?.hostel_final_fee !== undefined
    ) {
      return (
        numberValue(recipient?.batch_total_fee) +
        numberValue(recipient?.bus_final_fee) +
        numberValue(recipient?.hostel_final_fee)
      );
    }

    return numberValue(
      recipient?.final_fee ?? recipient?.all_session_total ?? 0,
    );
  };

  const isDueStudent = (recipient) => getDueAmount(recipient) > 0;
  const isPaidStudent = (recipient) => getDueAmount(recipient) <= 0;

  const getFeeStatusLabel = (recipient) =>
    isDueStudent(recipient) ? "Due" : "Paid";

  const getFeeStatusClass = (recipient) =>
    isDueStudent(recipient) ? "bg-label-danger" : "bg-label-success";

  const isBusStudent = (recipient) =>
    recipient?.is_bus === true ||
    recipient?.is_bus === 1 ||
    recipient?.is_bus === "1" ||
    String(recipient?.bus_student ?? "")
      .trim()
      .toLowerCase() === "yes";

  const isHostelStudent = (recipient) =>
    recipient?.is_hostel === true ||
    recipient?.is_hostel === 1 ||
    recipient?.is_hostel === "1" ||
    String(recipient?.hostel_student ?? "")
      .trim()
      .toLowerCase() === "yes";

  const matchesStudentType = (recipient, filter = studentTypeFilter) => {
    const bus = isBusStudent(recipient);
    const hostel = isHostelStudent(recipient);

    if (filter === "bus") return bus;
    if (filter === "hostel") return hostel;
    if (filter === "bus_hostel") return bus && hostel;
    if (filter === "day_scholar") return !bus && !hostel;
    return true;
  };

  const matchesRteFilter = (recipient, filter = studentRteFilter) => {
    const status = String(recipient?.rte_status ?? "")
      .trim()
      .toUpperCase();

    if (filter === "rte") return status === "RTE";
    if (filter === "non_rte") return status === "NON-RTE";
    return true;
  };

  const matchesFeeFilter = (recipient, filter = studentFeeFilter) => {
    if (filter === "due") return isDueStudent(recipient);
    if (filter === "paid") return isPaidStudent(recipient);
    return true;
  };

  const money = (value) =>
    numberValue(value).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const dynamicFields = useMemo(() => {
    if (!recipients.length) {
      return [];
    }

    const fieldMap = {};

    recipients.forEach((recipient) => {
      const flat = flattenObject(recipient);

      Object.entries(flat).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          fieldMap[key] = value;
        }
      });
    });

    /*
          Common useful fields ko top par rakhenge.
        */

    const priority = [
      "id",
      "name",
      "father_name",
      "mother_name",
      "mobile",
      "registration_no",
      "enquiry_no",
      "source",
      "category",
      "email",
      "village",
      "tehsil",
      "district",
      "state",

      // Staff
      "staff_type",
      "role_name",

      "fee_status",
      "rte_status",
      "is_bus",
      "is_hostel",
      "bus_student",
      "hostel_student",
      "student_type",

      "course",
      "batch",

      // Batch Fee
      "batch_total_fee",
      "batch_total_paid",
      "batch_total_due",

      // Bus Fee
      "bus_final_fee",
      "bus_paid",
      "bus_due",

      // Hostel Fee
      "hostel_final_fee",
      "hostel_paid",
      "hostel_due",

      // Grand Total
      "grand_final_fee",
      "grand_paid",
      "grand_due",

      // All Session Fee
      "all_session_total",
      "all_session_paid",
      "all_session_due",
    ];

    return Object.keys(fieldMap).sort((a, b) => {
      const ai = priority.indexOf(a);

      const bi = priority.indexOf(b);

      if (ai !== -1 && bi !== -1) {
        return ai - bi;
      }

      if (ai !== -1) return -1;
      if (bi !== -1) return 1;

      return a.localeCompare(b);
    });
  }, [recipients]);

  /* =========================================================
       TEMPLATE VARIABLES
    ========================================================= */

  useEffect(() => {
    if (!selectedTemplate) {
      setHeaderVariables([]);
      setBodyVariables([]);
      setButtonVariables([]);
      return;
    }

    setHeaderVariables(
      toVariables(
        findComponent(selectedTemplate, "HEADER")?.example?.header_text,
      ),
    );

    setBodyVariables(
      toVariables(findComponent(selectedTemplate, "BODY")?.example?.body_text),
    );

    setButtonVariables(toButtonVariables(selectedTemplate));
  }, [selectedTemplate]);

  /* =========================================================
       HEADER PREVIEW
    ========================================================= */

  useEffect(() => {
    if (!headerFile) {
      setHeaderPreview("");
      return;
    }

    const objectUrl = URL.createObjectURL(headerFile);

    setHeaderPreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [headerFile]);

  useEffect(() => {
    setHeaderFile(null);
  }, [selectedTemplate]);

  /* =========================================================
       RESOLVE VARIABLE
       
       Fixed:
         value directly jayegi.
  
       Dynamic:
         recipient se value jayegi.
    ========================================================= */

  const resolveVariable = (variable, recipient) => {
    if (!variable) {
      return "";
    }

    if (variable.mode === "dynamic") {
      return getDynamicValue(recipient, variable.dynamicField);
    }

    return variable.value?.trim() ?? "";
  };

  /* =========================================================
       PREVIEW
       
       Preview ke liye first recipient use karenge.
    ========================================================= */

  const previewRecipient =
    recipients?.[previewRecipientIndex] ?? recipients?.[0] ?? {};

  const replacePreviewVariables = (text, variables) => {
    if (!text) return "";

    return variables.reduce((acc, variable, index) => {
      const token = `{{${index + 1}}}`;

      const value = resolveVariable(variable, previewRecipient);

      return acc.split(token).join(value);
    }, text);
  };

  const previewHeader = replacePreviewVariables(
    findComponent(selectedTemplate, "HEADER")?.text ?? "",
    headerVariables,
  );

  const previewBody = replacePreviewVariables(
    findComponent(selectedTemplate, "BODY")?.text ?? "",
    bodyVariables,
  );

  const previewFooter = findComponent(selectedTemplate, "FOOTER")?.text ?? "";

  const recipientKey = (recipient, index) =>
    String(
      recipient?.student_id ??
        recipient?.id ??
        recipient?.registration_no ??
        recipient?.enquiry_no ??
        `${recipient?.mobile ?? recipient?.phone ?? "recipient"}-${index}`,
    );

  const allRecipientKeys = recipients.map(recipientKey);

  const effectiveSelectedKeys =
    selectedRecipientKeys === null ? allRecipientKeys : selectedRecipientKeys;

  const selectedRecipients = recipients.filter((recipient, index) =>
    effectiveSelectedKeys.includes(recipientKey(recipient, index)),
  );

  const normalizedStudentSearch = studentSearch.trim().toLowerCase();

  const searchableRecipients = recipients
    .map((recipient, index) => ({ recipient, index }))
    .filter(({ recipient }) => {
      if (!normalizedStudentSearch) return true;

      const searchable = [
        recipient?.name,
        recipient?.student_name,
        recipient?.father_name,
        recipient?.mother_name,
        recipient?.registration_no,
        recipient?.enquiry_no,
        recipient?.mobile,
        recipient?.phone,
        recipient?.course,
        recipient?.batch,
        recipient?.source,
        recipient?.rte_status,
        recipient?.student_type,
        recipient?.bus_student,
        recipient?.hostel_student,
        // Staff fields
        recipient?.staff_type,
        recipient?.role_name,
        recipient?.email,
        // Fee label sirf students ke liye (staff "paid" search me na aaye)
        isStaffType ? "" : getFeeStatusLabel(recipient),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedStudentSearch);
    });

  const filteredRecipients = searchableRecipients.filter(
    ({ recipient, index }) => {
      if (!showOnlySelected) return true;
      return effectiveSelectedKeys.includes(recipientKey(recipient, index));
    },
  );

  // Modal filters work together:
  // Student Type + Fee + RTE + Search.
  const modalFilteredRecipients = searchableRecipients
    .filter(
      ({ recipient }) =>
        matchesStudentType(recipient) &&
        matchesFeeFilter(recipient) &&
        matchesRteFilter(recipient),
    )
    .sort((a, b) => {
      const aDue = isDueStudent(a.recipient);
      const bDue = isDueStudent(b.recipient);

      if (aDue !== bDue) return aDue ? -1 : 1;

      return String(
        a.recipient?.name || a.recipient?.student_name || "",
      ).localeCompare(
        String(b.recipient?.name || b.recipient?.student_name || ""),
      );
    });

  const toggleRecipient = (index) => {
    const key = recipientKey(recipients[index], index);

    setSelectedRecipientKeys((current) => {
      const base = current === null ? allRecipientKeys : current;

      return base.includes(key)
        ? base.filter((item) => item !== key)
        : [...base, key];
    });
  };

  const selectAllRecipients = () => {
    setSelectedRecipientKeys(allRecipientKeys);
  };

  const selectVisibleRecipients = () => {
    setSelectedRecipientKeys(
      modalFilteredRecipients.map(({ recipient, index }) =>
        recipientKey(recipient, index),
      ),
    );
  };

  const addVisibleRecipients = () => {
    const visibleKeys = modalFilteredRecipients.map(({ recipient, index }) =>
      recipientKey(recipient, index),
    );

    setSelectedRecipientKeys((current) => {
      const base = current === null ? allRecipientKeys : current;
      return [...new Set([...base, ...visibleKeys])];
    });
  };

  const unselectVisibleRecipients = () => {
    const visibleSet = new Set(
      modalFilteredRecipients.map(({ recipient, index }) =>
        recipientKey(recipient, index),
      ),
    );

    setSelectedRecipientKeys((current) => {
      const base = current === null ? allRecipientKeys : current;
      return base.filter((key) => !visibleSet.has(key));
    });
  };

  const clearRecipientSelection = () => {
    setSelectedRecipientKeys([]);
  };

  const selectedCount = effectiveSelectedKeys.length;

  const isRecipientSelected = (index) =>
    effectiveSelectedKeys.includes(recipientKey(recipients[index], index));

  const previewStudentName =
    previewRecipient?.name ||
    previewRecipient?.student_name ||
    previewRecipient?.father_name ||
    `Sample ${entity.One}`;

  const previewButtons =
    findComponent(selectedTemplate, "BUTTONS")?.buttons ?? [];

  const buildStudentPreview = (recipient) => {
    const replaceVariablesForRecipient = (text, variables) => {
      if (!text) return "";

      return variables.reduce((acc, variable, index) => {
        const token = `{{${index + 1}}}`;
        const value = resolveVariable(variable, recipient);
        return acc.split(token).join(value);
      }, text);
    };

    return {
      header: replaceVariablesForRecipient(
        findComponent(selectedTemplate, "HEADER")?.text ?? "",
        headerVariables,
      ),
      body: replaceVariablesForRecipient(
        findComponent(selectedTemplate, "BODY")?.text ?? "",
        bodyVariables,
      ),
      footer: findComponent(selectedTemplate, "FOOTER")?.text ?? "",
      buttons: previewButtons.map((button, index) => ({
        ...button,
        value: button.url
          ? replaceVariablesForRecipient(
              button.url,
              buttonVariables.filter(
                (variable) => String(variable.index) === String(index),
              ),
            )
          : "",
      })),
    };
  };

  const dueStudentsCount = recipients.filter(isDueStudent).length;
  const paidStudentsCount = recipients.filter(isPaidStudent).length;
  const busStudentsCount = recipients.filter(isBusStudent).length;
  const hostelStudentsCount = recipients.filter(isHostelStudent).length;
  const busHostelStudentsCount = recipients.filter(
    (recipient) => isBusStudent(recipient) && isHostelStudent(recipient),
  ).length;
  const dayScholarStudentsCount = recipients.filter(
    (recipient) => !isBusStudent(recipient) && !isHostelStudent(recipient),
  ).length;
  const rteStudentsCount = recipients.filter((recipient) =>
    matchesRteFilter(recipient, "rte"),
  ).length;
  const nonRteStudentsCount = recipients.filter((recipient) =>
    matchesRteFilter(recipient, "non_rte"),
  ).length;

  const applyStudentTypeFilter = (filter) => {
    setStudentTypeFilter(filter);

    // Same behavior as Push Notification page:
    // clicking a type selects only matching students.
    const matchingKeys = recipients
      .map((recipient, index) => ({ recipient, index }))
      .filter(
        ({ recipient }) =>
          matchesStudentType(recipient, filter) &&
          matchesFeeFilter(recipient) &&
          matchesRteFilter(recipient),
      )
      .map(({ recipient, index }) => recipientKey(recipient, index));

    setSelectedRecipientKeys(matchingKeys);
  };

  const applyFeeFilter = (filter) => {
    setStudentFeeFilter(filter);

    const matchingKeys = recipients
      .map((recipient, index) => ({ recipient, index }))
      .filter(
        ({ recipient }) =>
          matchesStudentType(recipient) &&
          matchesFeeFilter(recipient, filter) &&
          matchesRteFilter(recipient),
      )
      .map(({ recipient, index }) => recipientKey(recipient, index));

    setSelectedRecipientKeys(matchingKeys);
  };

  const applyRteFilter = (filter) => {
    setStudentRteFilter(filter);

    const matchingKeys = recipients
      .map((recipient, index) => ({ recipient, index }))
      .filter(
        ({ recipient }) =>
          matchesStudentType(recipient) &&
          matchesFeeFilter(recipient) &&
          matchesRteFilter(recipient, filter),
      )
      .map(({ recipient, index }) => recipientKey(recipient, index));

    setSelectedRecipientKeys(matchingKeys);
  };

  /* =========================================================
       BUILD FORM DATA
       
       IMPORTANT:
       recipient parameter add kiya gaya hai.
       
       Dynamic variable har student ke hisab se
       alag value bhejega.
    ========================================================= */

  const buildFormData = (mobile, recipient) => {
    const formData = new FormData();

    formData.append("mobile", mobile);

    formData.append("category", selectedTemplate?.category ?? "");

    formData.append("template_name", selectedTemplate?.name ?? "");

    formData.append("template_lang", selectedTemplate?.language ?? "");

    /* =================================================
           HEADER FILE
        ================================================= */

    if (headerFile) {
      formData.append("header[type]", headerFormat ?? "IMAGE");

      formData.append("header[file]", headerFile);
    }

    /* =================================================
           HEADER VARIABLES
        ================================================= */

    headerVariables.forEach((variable) => {
      const value = resolveVariable(variable, recipient);

      if (value !== "") {
        formData.append("header_variables[]", value);
      }
    });

    /* =================================================
           BODY VARIABLES
        ================================================= */

    bodyVariables.forEach((variable) => {
      const value = resolveVariable(variable, recipient);

      if (value !== "") {
        formData.append("body_variables[]", value);
      }
    });

    /* =================================================
           BUTTON VARIABLES
        ================================================= */

    buttonVariables.forEach((variable, index) => {
      const value = resolveVariable(variable, recipient);

      if (value !== "") {
        formData.append(`buttons[${index}][index]`, variable.index);

        formData.append(`buttons[${index}][text]`, value);
      }
    });

    return formData;
  };

  /* =========================================================
       FIND RECIPIENT FOR MOBILE
    ========================================================= */
  useEffect(() => {
    recipientIndexesRef.current = {};
    setPreviewRecipientIndex(0);
    setSelectedRecipientKeys(null);
    setStudentSearch("");
    setShowOnlySelected(false);
    setStudentFeeFilter("all");
    setStudentRteFilter("all");
    setStudentTypeFilter("all");
  }, [recipients]);

  const recipientIndexesRef = useRef({});

  const findRecipientByMobile = (mobile) => {
    const matchingRecipients = recipients.filter(
      (recipient) => String(recipient.mobile) === String(mobile),
    );

    if (!matchingRecipients.length) {
      return { mobile };
    }

    const key = String(mobile);

    const currentIndex = recipientIndexesRef.current[key] ?? 0;

    const recipient =
      matchingRecipients[currentIndex % matchingRecipients.length];

    recipientIndexesRef.current[key] = currentIndex + 1;

    return recipient;
  };

  /* =========================================================
       SEND ONE
    ========================================================= */

  const sendToMobile = async (mobile, recipient) => {
    try {
      const response = await metaMessageSend(
        buildFormData(mobile, recipient ?? findRecipientByMobile(mobile)),
      );

      if (response?.data?.status !== false) {
        setProgress((prev) => ({
          ...prev,
          sent: prev.sent + 1,
          pending: Math.max(0, prev.pending - 1),
        }));

        return { mobile, success: true };
      }

      setProgress((prev) => ({
        ...prev,
        failed: prev.failed + 1,
        pending: Math.max(0, prev.pending - 1),
      }));

      return {
        mobile,
        success: false,
        message: response?.data?.message ?? "Rejected by server",
      };
    } catch (error) {
      setProgress((prev) => ({
        ...prev,
        failed: prev.failed + 1,
        pending: Math.max(0, prev.pending - 1),
      }));

      return {
        mobile,
        success: false,
        message:
          error?.response?.data?.message ?? error?.message ?? "Unknown error",
      };
    }
  };

  /* =========================================================
       VALIDATE VARIABLES
    ========================================================= */

  const validateVariables = () => {
    const allVariables = [
      ...headerVariables,
      ...bodyVariables,
      ...buttonVariables,
    ];

    for (const variable of allVariables) {
      if (variable.mode === "dynamic") {
        if (!variable.dynamicField) {
          return `Choose the ${entity.short} field for this value.`;
        }
      } else {
        if (!variable.value?.trim()) {
          return "Enter a value for this message field.";
        }
      }
    }

    return null;
  };

  /* =========================================================
       SEND MESSAGE
    ========================================================= */

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!selectedTemplate) {
      SweetAlertShow("warning", "Warning", "Select a template first");
      return;
    }

    const selectedMobiles = selectedRecipients
      .map((recipient) =>
        String(recipient?.mobile ?? recipient?.phone ?? "").trim(),
      )
      .filter(Boolean);

    const { valid: selectedValidMobiles, invalid: selectedInvalidMobiles } =
      parseMobiles(selectedMobiles.join(", "), true);

    if (!selectedValidMobiles.length) {
      SweetAlertShow(
        "warning",
        "Warning",
        `Select at least one ${entity.one} with a valid 10-digit mobile number`,
      );
      return;
    }

    if (selectedInvalidMobiles.length) {
      SweetAlertShow(
        "info",
        isStaffType ? "Some staff skipped" : "Some students skipped",
        `${selectedInvalidMobiles.length} selected ${entity.one}(s) have an invalid mobile number.`,
      );
    }

    if (needsHeaderFile && !headerFile) {
      SweetAlertShow(
        "warning",
        "Warning",
        `This template needs a ${String(headerFormat).toLowerCase()} header`,
      );
      return;
    }

    const variableError = validateVariables();

    if (variableError) {
      SweetAlertShow("warning", "Variable Required", variableError);

      return;
    }

    stopRef.current = false;

    setSending(true);

    setFailures([]);

    setProgress({
      total: selectedValidMobiles.length,
      sent: 0,
      failed: 0,
      pending: selectedValidMobiles.length,
    });

    sendSessionRef.current = {
      startedAt: new Date().toISOString(),
      total: selectedValidMobiles.length,
      sent: 0,
      failed: 0,
      pending: selectedValidMobiles.length,
    };

    try {
      localStorage.setItem(
        SEND_STORAGE_KEY,
        JSON.stringify({
          ...sendSessionRef.current,
          status: "sending",
        }),
      );
    } catch {}

    const collected = [];

    try {
      for (let i = 0; i < selectedValidMobiles.length; i += BATCH_SIZE) {
        if (stopRef.current) {
          break;
        }

        const sendableRecipients = selectedRecipients.filter((recipient) =>
          MOBILE_PATTERN.test(
            String(recipient?.mobile ?? recipient?.phone ?? "").trim(),
          ),
        );

        const chunk = sendableRecipients.slice(i, i + BATCH_SIZE);

        const results = await Promise.all(
          chunk.map((recipient) =>
            sendToMobile(
              String(recipient?.mobile ?? recipient?.phone ?? "").trim(),
              recipient,
            ),
          ),
        );

        const chunkFailures = results.filter((result) => !result.success);

        if (chunkFailures.length) {
          collected.push(...chunkFailures);

          setFailures((prev) => [...prev, ...chunkFailures]);
        }

        if (i + BATCH_SIZE < selectedValidMobiles.length) {
          await sleep(BATCH_DELAY_MS);
        }
      }

      SweetAlertShow(
        stopRef.current ? "info" : "success",
        stopRef.current ? "Sending stopped" : "Sending finished",
        stopRef.current
          ? `Sending was stopped.\n\nTotal: ${selectedValidMobiles.length}\nSent: ${progress.sent}\nFailed: ${collected.length}\nPending: ${Math.max(0, selectedValidMobiles.length - progress.sent - collected.length)}`
          : `All messages were processed successfully.\n\nTotal: ${selectedValidMobiles.length}\nFailed: ${collected.length}`,
      );

      try {
        localStorage.removeItem(SEND_STORAGE_KEY);
      } catch {}
    } catch (error) {
      showError(error);
    } finally {
      setSending(false);
      stopRef.current = false;
      sendSessionRef.current = null;
    }
  };

  useEffect(() => {
    if (!sending || !sendSessionRef.current) return;

    sendSessionRef.current = {
      ...sendSessionRef.current,
      sent: progress.sent,
      failed: progress.failed,
      pending: progress.pending,
      total: progress.total,
    };

    try {
      localStorage.setItem(
        SEND_STORAGE_KEY,
        JSON.stringify({
          ...sendSessionRef.current,
          status: "sending",
          updatedAt: new Date().toISOString(),
        }),
      );
    } catch {}
  }, [sending, progress]);

  /* =========================================================
       STOP
    ========================================================= */

  const handleStop = () => {
    stopRef.current = true;

    // The current API request cannot be cancelled safely here; it will
    // finish, then the loop will not start another batch.
    try {
      localStorage.removeItem(SEND_STORAGE_KEY);
    } catch {}
  };

  /* =========================================================
       DOWNLOAD FAILURES
    ========================================================= */

  const downloadFailures = () => {
    const csv = [
      "mobile,reason",

      ...failures.map(
        (failure) =>
          `${failure.mobile},"${String(failure.message).replace(/"/g, "'")}"`,
      ),
    ].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");

    link.href = url;

    link.download = `failed-numbers-${Date.now()}.csv`;

    link.click();

    URL.revokeObjectURL(url);
  };

  /* =========================================================
       PROGRESS
    ========================================================= */

  const completed = progress.sent + progress.failed;

  const percent = progress.total
    ? Math.round((completed / progress.total) * 100)
    : 0;

  /* =========================================================
       SELECT
    ========================================================= */

  const toggleSelection = (value, setter) => {
    const id = String(value);

    setter((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  /* =========================================================
       UPDATE VARIABLE
    ========================================================= */

  const updateVariable = (setter, index, changes) => {
    setter((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              ...changes,
            }
          : item,
      ),
    );
  };

  /* =========================================================
       DETAIL
    ========================================================= */

  const Detail = ({ label, value }) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    return (
      <div className="mb-1">
        <small className="text-muted">{label}:</small> <span>{value}</span>
      </div>
    );
  };

  /* =========================================================
       UI HELPERS
    ========================================================= */

  const workflowStep = (number, title, description, active, complete) => (
    <div className="d-flex align-items-start gap-3">
      <div
        className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${
          complete
            ? "bg-success text-white"
            : active
              ? "bg-primary text-white"
              : "bg-light text-muted"
        }`}
        style={{ width: 34, height: 34, fontWeight: 700 }}
      >
        {complete ? "✓" : number}
      </div>
      <div>
        <div className="fw-semibold">{title}</div>
        <div className="small text-muted">{description}</div>
      </div>
    </div>
  );

  const renderSelectionList = ({
    title,
    items,
    selected,
    setter,
    idPrefix,
    labelKey,
  }) => (
    <div className="border rounded-3 p-3 bg-light-subtle">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <span className="fw-semibold">{title}</span>
        <span className="badge bg-label-primary">
          {selected.length} selected
        </span>
      </div>

      {items.length === 0 ? (
        <div className="text-muted small py-2">
          No {title.toLowerCase()} found.
        </div>
      ) : (
        <div className="row g-2" style={{ maxHeight: 230, overflowY: "auto" }}>
          {items.map((item) => {
            const id = String(item.id);
            const label =
              typeof labelKey === "function" ? labelKey(item) : item[labelKey];

            return (
              <div className="col-md-6" key={item.id}>
                <label
                  className={`d-flex align-items-center gap-2 border rounded-3 p-2 h-100 ${
                    selected.includes(id)
                      ? "border-primary bg-white shadow-sm"
                      : "bg-white"
                  }`}
                  style={{ cursor: "pointer" }}
                >
                  <input
                    className="form-check-input mt-0"
                    type="checkbox"
                    checked={selected.includes(id)}
                    onChange={() => toggleSelection(id, setter)}
                  />
                  <span className="small fw-medium">{label}</span>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  /* =========================================================
       JSX
    ========================================================= */

  return (
    <>
      {loading && <LoadingDiv />}

      {sending && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            background: "rgba(0,0,0,.58)",
            zIndex: 99999,
            padding: 16,
          }}
        >
          <div
            className="card shadow-lg border-0"
            style={{ width: "min(560px, 100%)" }}
          >
            <div className="card-body p-4">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div
                  className="spinner-border text-primary"
                  role="status"
                  aria-hidden="true"
                />
                <div>
                  <h5 className="mb-1">Sending WhatsApp messages...</h5>
                  <div className="small text-muted">
                    Please stay on this page until sending is complete.
                  </div>
                </div>
              </div>

              <div className="progress mb-3" style={{ height: 10 }}>
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  style={{ width: `${percent}%` }}
                />
              </div>

              <div className="row text-center g-2 mb-3">
                <div className="col-3">
                  <div className="small text-muted">Total</div>
                  <strong>{progress.total}</strong>
                </div>
                <div className="col-3">
                  <div className="small text-muted">Sent</div>
                  <strong className="text-success">{progress.sent}</strong>
                </div>
                <div className="col-3">
                  <div className="small text-muted">Failed</div>
                  <strong className="text-danger">{progress.failed}</strong>
                </div>
                <div className="col-3">
                  <div className="small text-muted">Pending</div>
                  <strong className="text-primary">{progress.pending}</strong>
                </div>
              </div>

              <div className="alert alert-warning py-2 mb-3">
                <i className="ri-error-warning-line me-1" />
                Do not refresh or close this page. If you leave before all API
                responses are received, browser-based sending will stop.
              </div>

              <button
                type="button"
                className="btn btn-outline-danger w-100"
                onClick={handleStop}
              >
                Stop after current request
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  {/* =================================================
                                       PAGE HEADER
                                    ================================================= */}
                  <div className="col-12 mb-4">
                    <div className="card border-0 shadow-sm overflow-hidden">
                      <div className="card-body p-4">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span
                                className="rounded-circle d-inline-flex align-items-center justify-content-center bg-success text-white"
                                style={{
                                  width: 42,
                                  height: 42,
                                  fontSize: 20,
                                }}
                              >
                                ✓
                              </span>
                              <h4 className="mb-0">
                                Send WhatsApp Notification
                              </h4>
                            </div>
                            <p className="text-muted mb-0 ms-5">
                              Select a template, choose your audience,
                              personalize the message, preview it, and send.
                            </p>
                          </div>

                          <div className="d-flex gap-2 flex-wrap">
                            <span className="badge bg-label-primary fs-6 px-3 py-2">
                              {selectedCount} selected
                            </span>
                            {invalidMobiles.length > 0 && (
                              <span className="badge bg-label-warning fs-6 px-3 py-2">
                                {invalidMobiles.length} skipped
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="row g-3 mt-3">
                          <div className="col-md-4">
                            {workflowStep(
                              1,
                              "Choose template",
                              "Pick an approved WhatsApp template.",
                              !selectedTemplate,
                              !!selectedTemplate,
                            )}
                          </div>
                          <div className="col-md-4">
                            {workflowStep(
                              2,
                              "Choose audience",
                              `Select the ${entity.many} who should receive it.`,
                              !!selectedTemplate && !recipients.length,
                              !!selectedTemplate && !!recipients.length,
                            )}
                          </div>
                          <div className="col-md-4">
                            {workflowStep(
                              3,
                              "Personalize",
                              `Map ${entity.short} fields to template values.`,
                              !!recipients.length,
                              !!recipients.length,
                            )}
                          </div>
                          <div className="col-md-4 mt-3 mt-md-0">
                            {workflowStep(
                              4,
                              "Select & send",
                              `Choose specific ${entity.many}, preview, then send.`,
                              !!recipients.length,
                              false,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                                       TEMPLATE FIRST
                                    ================================================= */}
                  <div className="col-12 mb-4">
                    <div
                      className={`card shadow-sm ${
                        selectedTemplate ? "border-success" : "border-primary"
                      }`}
                    >
                      <div className="card-header bg-transparent py-3">
                        <div className="d-flex align-items-center justify-content-between gap-2">
                          <div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="badge bg-primary rounded-pill">
                                1
                              </span>
                              <h5 className="mb-0">Choose message template</h5>
                            </div>
                            <small className="text-muted">
                              Start here. Only approved templates are available.
                            </small>
                          </div>

                          {selectedTemplate && (
                            <span className="badge bg-label-success">
                              Approved
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="card-body">
                        <div className="row align-items-center g-3">
                          <div className="col-lg-8">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={selectedTemplate?.name ?? ""}
                                onChange={(e) => {
                                  const template =
                                    templates.find(
                                      (item) => item.name === e.target.value,
                                    ) ?? null;

                                  setSelectedTemplate(template);
                                  setSelectedBatches([]);
                                  setSelectedCourses([]);
                                  setSelectedSources([]);
                                  setMobileNos("");
                                  setRecipients([]);
                                  setSelectedRecipientKeys(null);
                                  setStudentSearch("");
                                  setShowOnlySelected(false);
                                }}
                              >
                                <option value="">
                                  Select approved template
                                </option>

                                {templates.map((template) => (
                                  <option
                                    key={template.id ?? template.name}
                                    value={template.name}
                                  >
                                    {template.name} ({template.language})
                                  </option>
                                ))}
                              </select>
                              <label>WhatsApp template</label>
                            </div>
                          </div>

                          <div className="col-lg-4">
                            {selectedTemplate ? (
                              <div className="rounded-3 bg-light p-3">
                                <div className="small text-muted">
                                  Selected template
                                </div>
                                <div className="fw-semibold text-primary text-break">
                                  {selectedTemplate.name}
                                </div>
                                <div className="small text-muted mt-1">
                                  {selectedTemplate.language} ·{" "}
                                  {selectedTemplate.category}
                                </div>
                              </div>
                            ) : (
                              <div className="rounded-3 bg-light p-3 text-muted small">
                                Choose a template to continue.
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                                       AUDIENCE SECOND
                                    ================================================= */}
                  <div className="col-lg-8 mb-4">
                    <div
                      className={`card shadow-sm ${
                        selectedTemplate ? "border-primary" : "opacity-75"
                      }`}
                    >
                      <div className="card-header bg-transparent py-3">
                        <div className="d-flex align-items-center gap-2">
                          <span className="badge bg-primary rounded-pill">
                            2
                          </span>
                          <div>
                            <h5 className="mb-0">Choose audience</h5>
                            <small className="text-muted">
                              Tell us who should receive this message.
                            </small>
                          </div>
                        </div>
                      </div>

                      <div className="card-body">
                        {!selectedTemplate ? (
                          <div className="alert alert-light border mb-0">
                            <i className="ri-information-line me-2" />
                            Select a template above before choosing{" "}
                            {entity.many}.
                          </div>
                        ) : (
                          <>
                            <div className="row g-3">
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    value={filters.type}
                                    onChange={(e) => {
                                      const type = e.target.value;

                                      const activeSession = sessions.find(
                                        (session) =>
                                          Number(session.status) === 1,
                                      );

                                      setFilters({
                                        type,
                                        session_id: activeSession
                                          ? String(activeSession.id)
                                          : "",
                                      });

                                      setSelectedBatches([]);
                                      setSelectedCourses([]);
                                      setSelectedSources([]);
                                      setSelectedBusId("");
                                      setMobileNos("");
                                      setRecipients([]);
                                      setSelectedRecipientKeys(null);
                                    }}
                                  >
                                    <option value="">Select audience</option>

                                    {AUDIENCE_TYPES.map((type) => (
                                      <option key={type} value={type}>
                                        {type}
                                      </option>
                                    ))}
                                  </select>
                                  <label>Who should receive it?</label>
                                </div>
                              </div>

                              {(isBatchType ||
                                isEnquiryType ||
                                isMarketingType ||
                                isBusType) && (
                                <div className="col-md-6">
                                  <div className="form-floating form-floating-outline">
                                    <select
                                      className="form-select"
                                      value={filters.session_id ?? ""}
                                      onChange={(e) =>
                                        setFilters((prev) => ({
                                          ...prev,
                                          session_id: e.target.value,
                                        }))
                                      }
                                    >
                                      <option value="">Select session</option>

                                      {sessions.map((session) => (
                                        <option
                                          key={session.id}
                                          value={session.id}
                                        >
                                          {session.session}
                                          {Number(session.status) === 1
                                            ? " (active)"
                                            : ""}
                                        </option>
                                      ))}
                                    </select>
                                    <label>Session</label>
                                  </div>
                                </div>
                              )}
                            </div>

                            {!filters.type && (
                              <div className="text-center py-4 text-muted">
                                <div
                                  className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-2"
                                  style={{
                                    width: 54,
                                    height: 54,
                                  }}
                                >
                                  <i className="ri-group-line fs-3" />
                                </div>
                                <div className="fw-semibold">
                                  Select an audience
                                </div>
                                <div className="small">
                                  Your recipient list will appear here
                                  automatically.
                                </div>
                              </div>
                            )}

                            {isBatchType && (
                              <div className="mt-3">
                                {renderSelectionList({
                                  title: "Batches",
                                  items: batches,
                                  selected: selectedBatches,
                                  setter: setSelectedBatches,
                                  idPrefix: "batch",
                                  labelKey: "title",
                                })}
                              </div>
                            )}

                            {isEnquiryType && (
                              <div className="mt-3">
                                {renderSelectionList({
                                  title: "Courses",
                                  items: courses,
                                  selected: selectedCourses,
                                  setter: setSelectedCourses,
                                  idPrefix: "course",
                                  labelKey: "title",
                                })}
                              </div>
                            )}

                            {isMarketingType && (
                              <div className="mt-3">
                                {renderSelectionList({
                                  title: "Marketing sources",
                                  items: sources,
                                  selected: selectedSources,
                                  setter: setSelectedSources,
                                  idPrefix: "source",
                                  labelKey: "source_name",
                                })}
                              </div>
                            )}

                            {isBusType && (
                              <div className="mt-3">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                                  <div>
                                    <h6 className="mb-1">Select a bus</h6>
                                    <small className="text-muted">
                                      Choose a bus to load its registered
                                      students.
                                    </small>
                                  </div>

                                  <span className="badge bg-label-primary">
                                    {buses.length} buses
                                  </span>
                                </div>

                                {!buses.length ? (
                                  <div className="text-center border rounded-3 p-4">
                                    <i className="ri-bus-line fs-1 text-muted d-block mb-2" />
                                    <div className="fw-semibold">
                                      No buses found
                                    </div>
                                    <small className="text-muted">
                                      No buses are available for the selected
                                      session.
                                    </small>
                                  </div>
                                ) : (
                                  <div className="row g-3">
                                    {buses.map((bus) => {
                                      const isSelected =
                                        String(selectedBusId) ===
                                        String(bus.id);

                                      return (
                                        <div
                                          className="col-md-6 col-xl-4"
                                          key={bus.id}
                                        >
                                          <button
                                            type="button"
                                            className={`card w-100 h-100 text-start ${
                                              isSelected
                                                ? "border-primary shadow-sm"
                                                : "border"
                                            }`}
                                            onClick={() => {
                                              setSelectedBusId(String(bus.id));
                                              setMobileNos("");
                                              setRecipients([]);
                                              setSelectedRecipientKeys(null);
                                              setStudentSearch("");
                                              setPreviewRecipientIndex(0);
                                            }}
                                            style={{
                                              cursor: "pointer",
                                              background: "var(--bs-card-bg)",
                                            }}
                                          >
                                            <div className="card-body">
                                              <div className="d-flex align-items-start gap-3">
                                                <div
                                                  className="rounded-3 bg-label-primary d-flex align-items-center justify-content-center flex-shrink-0"
                                                  style={{
                                                    width: 46,
                                                    height: 46,
                                                  }}
                                                >
                                                  <i className="ri-bus-line fs-3" />
                                                </div>

                                                <div className="flex-grow-1 min-w-0">
                                                  <div className="fw-semibold">
                                                    {bus.bus_number ||
                                                      "Unnamed bus"}
                                                  </div>

                                                  <div className="small text-muted mt-1">
                                                    <i className="ri-user-line me-1" />
                                                    {bus.driver_name ||
                                                      "Driver not assigned"}
                                                  </div>

                                                  {bus.driver_number && (
                                                    <div className="small text-muted mt-1">
                                                      <i className="ri-phone-line me-1" />
                                                      {bus.driver_number}
                                                    </div>
                                                  )}
                                                </div>

                                                <i
                                                  className={`${
                                                    isSelected
                                                      ? "ri-checkbox-circle-fill text-primary"
                                                      : "ri-circle-line text-muted"
                                                  } fs-4`}
                                                />
                                              </div>

                                              {isSelected && (
                                                <div className="mt-3">
                                                  <span className="badge bg-label-primary">
                                                    Selected
                                                  </span>
                                                </div>
                                              )}
                                            </div>
                                          </button>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Mobile numbers are intentionally hidden.
                                                            They are generated from the selected audience. */}
                            {recipients.length > 0 && (
                              <div className="mt-3">
                                <div className="rounded-3 border bg-light p-3 d-flex align-items-center justify-content-between gap-3">
                                  <div>
                                    <div className="fw-semibold">
                                      Audience ready
                                    </div>
                                    <div className="small text-muted">
                                      {selectedCount} of {recipients.length}{" "}
                                      {entity.many} selected.
                                      {!isStaffType &&
                                        " Due students are available in the student selection modal."}
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <span className="badge bg-success">
                                      {selectedCount} ready
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-primary"
                                      onClick={() => setShowStudentModal(true)}
                                    >
                                      Select / Edit {entity.Many}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {invalidMobiles.length > 0 && (
                              <div className="small text-warning mt-2">
                                {invalidMobiles.length} invalid number(s) will
                                be skipped automatically.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                                       LIVE PREVIEW SIDEBAR
                                    ================================================= */}
                  <div className="col-lg-4 mb-4">
                    <div
                      className="card shadow-sm border-0 sticky-top"
                      style={{ top: "5rem", zIndex: 1 }}
                    >
                      <div className="card-header bg-transparent py-3">
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h5 className="mb-0">Live message preview</h5>
                            <small className="text-muted">
                              See exactly what one {entity.one} receives.
                            </small>
                          </div>
                          <span className="badge bg-label-success">
                            WhatsApp
                          </span>
                        </div>
                      </div>

                      <div className="card-body p-0">
                        {recipients.length > 0 && (
                          <div className="p-3 border-bottom">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                value={previewRecipientIndex}
                                onChange={(e) =>
                                  setPreviewRecipientIndex(
                                    Number(e.target.value),
                                  )
                                }
                              >
                                {recipients
                                  .slice(0, 100)
                                  .map((recipient, index) => (
                                    <option
                                      key={`${recipient.mobile}-${index}`}
                                      value={index}
                                    >
                                      {recipient.name ||
                                        recipient.student_name ||
                                        entity.One}{" "}
                                      · {recipient.mobile || "No mobile"}
                                    </option>
                                  ))}
                              </select>
                              <label>Preview {entity.one}</label>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mt-2">
                              <small className="text-muted">
                                {selectedCount} selected · Showing recipient{" "}
                                {previewRecipientIndex + 1} of{" "}
                                {recipients.length}
                              </small>
                              <small className="fw-semibold">
                                {previewStudentName}
                              </small>
                            </div>
                          </div>
                        )}

                        <div
                          className="overflow-hidden"
                          style={{
                            background: "#efeae2",
                          }}
                        >
                          <div
                            className="d-flex align-items-center gap-2 px-3"
                            style={{
                              background: "#075E54",
                              color: "white",
                              height: 54,
                            }}
                          >
                            <div
                              className="rounded-circle bg-white text-success d-flex align-items-center justify-content-center fw-bold"
                              style={{
                                width: 34,
                                height: 34,
                              }}
                            >
                              W
                            </div>
                            <div className="lh-sm">
                              <div className="fw-semibold">Message preview</div>
                              <small>WhatsApp</small>
                            </div>
                          </div>

                          <div
                            className="p-3"
                            style={{
                              minHeight: 340,
                              maxHeight: "58vh",
                              overflowY: "auto",
                            }}
                          >
                            {!selectedTemplate ? (
                              <div className="text-center text-muted pt-5">
                                <i className="ri-message-3-line fs-1 d-block mb-2" />
                                Select a template to preview the message.
                              </div>
                            ) : (
                              <div
                                className="bg-white rounded-3 p-2 shadow-sm ms-auto"
                                style={{
                                  maxWidth: "95%",
                                  fontSize: 14,
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {headerPreview &&
                                  headerFile?.type?.startsWith("image/") && (
                                    <img
                                      src={headerPreview}
                                      alt=""
                                      className="rounded-2 mb-2 d-block"
                                      style={{
                                        width: "100%",
                                        maxHeight: 220,
                                        objectFit: "contain",
                                      }}
                                    />
                                  )}

                                {previewHeader && (
                                  <div className="fw-semibold mb-1">
                                    {previewHeader}
                                  </div>
                                )}

                                <div>
                                  {previewBody ||
                                    findComponent(selectedTemplate, "BODY")
                                      ?.text ||
                                    "Your message preview will appear here."}
                                </div>

                                {previewFooter && (
                                  <div
                                    className="text-muted mt-2"
                                    style={{
                                      fontSize: 12,
                                    }}
                                  >
                                    {previewFooter}
                                  </div>
                                )}

                                <div
                                  className="text-end text-muted mt-1"
                                  style={{
                                    fontSize: 11,
                                  }}
                                >
                                  {new Date().toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </div>

                                {previewButtons.length > 0 && (
                                  <div className="border-top mt-2 pt-1">
                                    {previewButtons.map((button, index) => (
                                      <div
                                        key={index}
                                        className="text-center py-1 text-primary"
                                      >
                                        {button.text}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {recipients.length > 0 && (
                          <div className="p-3 border-top bg-white">
                            <div className="small text-muted mb-1">
                              This preview uses real data from:
                            </div>
                            <div className="fw-semibold">
                              {previewStudentName}
                            </div>
                            <div className="small text-muted">
                              {previewRecipient?.mobile || "No mobile"}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                                       VARIABLE MAPPING - THIRD STEP
                                    ================================================= */}
                  <div className="col-12 mb-4">
                    <div
                      className={`card shadow-sm ${
                        selectedTemplate && recipients.length
                          ? "border-primary"
                          : ""
                      }`}
                    >
                      <div className="card-header bg-transparent py-3">
                        <div className="d-flex align-items-center justify-content-between gap-3">
                          <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary rounded-pill">
                              3
                            </span>
                            <div>
                              <h5 className="mb-0">Personalize message</h5>
                              <small className="text-muted">
                                Map template variables to {entity.short} data.
                                The variable numbers stay hidden; you only
                                choose the data.
                              </small>
                            </div>
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-primary"
                            disabled={!selectedTemplate || !recipients.length}
                            onClick={() => setShowVariableModal(true)}
                          >
                            <i className="ri-edit-line me-1" />
                            Select Variables
                          </button>

                          {bodyVariables.length +
                            headerVariables.length +
                            buttonVariables.length >
                            0 && (
                            <span className="badge bg-label-primary">
                              {bodyVariables.length +
                                headerVariables.length +
                                buttonVariables.length}{" "}
                              variables
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="card-body">
                        {!selectedTemplate ? (
                          <div className="alert alert-light border mb-0">
                            Select a template first.
                          </div>
                        ) : !recipients.length ? (
                          <div className="text-center py-5">
                            <div
                              className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center mb-3"
                              style={{ width: 60, height: 60 }}
                            >
                              <i className="ri-group-line fs-2 text-muted" />
                            </div>
                            <div className="fw-semibold">
                              Choose your audience first
                            </div>
                            <div className="small text-muted mt-1">
                              Once {entity.many} are loaded, their fields will
                              appear here for easy personalization.
                            </div>
                          </div>
                        ) : (
                          <>
                            {needsHeaderFile && (
                              <div className="mb-4 rounded-3 border p-3">
                                <div className="fw-semibold mb-1">
                                  Template media
                                </div>
                                <div className="small text-muted mb-2">
                                  This template needs a{" "}
                                  {String(headerFormat).toLowerCase()} file.
                                </div>
                                <input
                                  type="file"
                                  className="form-control"
                                  accept={
                                    headerFormat === "IMAGE"
                                      ? "image/*"
                                      : headerFormat === "VIDEO"
                                        ? "video/*"
                                        : ".pdf,.doc,.docx"
                                  }
                                  onChange={(e) =>
                                    setHeaderFile(e.target.files?.[0] ?? null)
                                  }
                                />
                              </div>
                            )}

                            {headerVariables.length > 0 && (
                              <div className="mb-4">
                                <div className="fw-semibold mb-2">
                                  Header data
                                </div>
                                <div className="row">
                                  {headerVariables.map((variable, index) => (
                                    <VariableInput
                                      key={`header-${index}`}
                                      label="Header value"
                                      variable={variable}
                                      index={index}
                                      setter={setHeaderVariables}
                                      dynamicFields={dynamicFields}
                                      updateVariable={updateVariable}
                                      formatDynamicLabel={formatDynamicLabel}
                                      entityLabel={entity.short}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}

                            {bodyVariables.length > 0 && (
                              <div className="mb-4">
                                <div className="fw-semibold mb-2">
                                  Message data
                                </div>
                                <div className="row">
                                  {bodyVariables.map((variable, index) => (
                                    <VariableInput
                                      key={`body-${index}`}
                                      label="Message value"
                                      variable={variable}
                                      index={index}
                                      setter={setBodyVariables}
                                      dynamicFields={dynamicFields}
                                      updateVariable={updateVariable}
                                      formatDynamicLabel={formatDynamicLabel}
                                      entityLabel={entity.short}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}

                            {buttonVariables.length > 0 && (
                              <div className="mb-2">
                                <div className="fw-semibold mb-2">
                                  Button data
                                </div>
                                <div className="row">
                                  {buttonVariables.map((variable, index) => (
                                    <VariableInput
                                      key={`button-${index}`}
                                      label={
                                        variable.buttonText || "Button value"
                                      }
                                      variable={variable}
                                      index={index}
                                      setter={setButtonVariables}
                                      dynamicFields={dynamicFields}
                                      updateVariable={updateVariable}
                                      formatDynamicLabel={formatDynamicLabel}
                                      entityLabel={entity.short}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}

                            {headerVariables.length === 0 &&
                              bodyVariables.length === 0 &&
                              buttonVariables.length === 0 && (
                                <div className="rounded-3 border bg-light p-4 text-center">
                                  <div className="fw-semibold">
                                    No variables required
                                  </div>
                                  <div className="small text-muted">
                                    This template is ready to send as-is.
                                  </div>
                                </div>
                              )}

                            {dynamicFields.length === 0 &&
                              (bodyVariables.length > 0 ||
                                headerVariables.length > 0 ||
                                buttonVariables.length > 0) && (
                                <div className="alert alert-warning mt-3 mb-0">
                                  Select an audience and load {entity.many}{" "}
                                  first. Dynamic {entity.short} fields will then
                                  appear here.
                                </div>
                              )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                                       STEP 4 — STUDENT SELECTION MODAL
                                    ================================================= */}
                  {recipients.length > 0 && (
                    <div className="col-12 mb-4">
                      <div className="card shadow-sm border-primary">
                        <div className="card-body">
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                            <div>
                              <div className="d-flex align-items-center gap-2">
                                <span className="badge bg-primary rounded-pill">
                                  4
                                </span>
                                <h5 className="mb-0">
                                  {entity.Many} & message preview
                                </h5>
                              </div>
                              <div className="small text-muted mt-1">
                                {selectedCount} of {recipients.length}{" "}
                                {entity.many} selected.
                                {!isStaffType && (
                                  <>
                                    {" "}
                                    Due: {dueStudentsCount} · Paid:{" "}
                                    {paidStudentsCount}
                                  </>
                                )}
                              </div>
                            </div>

                            <div className="d-flex flex-wrap gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-primary"
                                onClick={() => setShowStudentModal(true)}
                              >
                                <i className="ri-group-line me-1" />
                                Select / Edit {entity.Many}
                              </button>

                              <button
                                type="button"
                                className="btn btn-outline-success"
                                onClick={() => setShowPreviewModal(true)}
                                disabled={!selectedTemplate || !selectedCount}
                              >
                                <i className="ri-eye-line me-1" />
                                Preview All Messages
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* =================================================
                                       SELECTED STUDENT / STAFF SUMMARY
                                    ================================================= */}
                  {recipients.length > 0 && (
                    <div className="col-12 mb-4">
                      <div className="card shadow-sm">
                        <div className="card-header bg-transparent py-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h5 className="mb-0">Selected {entity.many}</h5>
                              <small className="text-muted">
                                Only these {entity.many} will receive the
                                message.
                              </small>
                            </div>
                            <span className="badge bg-label-primary">
                              {selectedCount} / {recipients.length}
                            </span>
                          </div>
                        </div>

                        <div className="card-body p-0">
                          <div
                            className="table-responsive"
                            style={{ maxHeight: 300 }}
                          >
                            <table className="table table-hover align-middle mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>#</th>
                                  <th>{entity.One}</th>
                                  <th>Mobile</th>
                                  {isStaffType ? (
                                    <>
                                      <th>Type</th>
                                      <th>Role</th>
                                    </>
                                  ) : (
                                    <>
                                      <th>Fee</th>
                                      <th>Due</th>
                                    </>
                                  )}
                                  <th>Preview</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedRecipients
                                  .slice(0, 100)
                                  .map((recipient, index) => (
                                    <tr key={recipientKey(recipient, index)}>
                                      <td>{index + 1}</td>
                                      <td>
                                        <div className="fw-semibold">
                                          {recipient.name ??
                                            recipient.student_name ??
                                            "-"}
                                        </div>
                                        <small className="text-muted">
                                          {recipient.registration_no ??
                                            recipient.enquiry_no ??
                                            "-"}
                                        </small>
                                      </td>
                                      <td>
                                        {recipient.mobile ??
                                          recipient.phone ??
                                          "-"}
                                      </td>
                                      {isStaffType ? (
                                        <>
                                          <td>{recipient.staff_type || "-"}</td>
                                          <td>{recipient.role_name || "-"}</td>
                                        </>
                                      ) : (
                                        <>
                                          <td>
                                            <span
                                              className={`badge ${getFeeStatusClass(recipient)}`}
                                            >
                                              {getFeeStatusLabel(recipient)}
                                            </span>
                                          </td>
                                          <td>{recipient.total_due ?? "-"}</td>
                                        </>
                                      )}
                                      <td>
                                        <button
                                          type="button"
                                          className="btn btn-sm btn-outline-primary"
                                          onClick={() => {
                                            const originalIndex =
                                              recipients.findIndex(
                                                (item, originalIndex) =>
                                                  recipientKey(
                                                    item,
                                                    originalIndex,
                                                  ) ===
                                                  recipientKey(
                                                    recipient,
                                                    index,
                                                  ),
                                              );
                                            setPreviewRecipientIndex(
                                              originalIndex >= 0
                                                ? originalIndex
                                                : 0,
                                            );
                                            setShowPreviewModal(true);
                                          }}
                                        >
                                          <i className="ri-eye-line me-1" />
                                          View
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>

                          {selectedRecipients.length > 100 && (
                            <div className="p-2 text-center small text-muted border-top">
                              Showing first 100 selected {entity.many}. Use
                              Preview All Messages to view the complete list.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* =================================================
                                       SEND + PROGRESS
                                    ================================================= */}
                  <div className="col-12 mb-4">
                    <div className="card shadow-sm border-0">
                      <div className="card-body">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                          <div>
                            <div className="fw-semibold">Ready to send?</div>
                            <div className="small text-muted">
                              {selectedCount
                                ? `${selectedCount} selected ${entity.one}(s) will receive the selected template.`
                                : `Select at least one ${entity.one} first.`}
                            </div>
                          </div>

                          <div className="d-flex gap-2">
                            {sending && (
                              <button
                                type="button"
                                className="btn btn-outline-danger"
                                onClick={handleStop}
                              >
                                Stop sending
                              </button>
                            )}

                            <button
                              type="button"
                              className="btn btn-primary px-4"
                              disabled={
                                sending || !selectedTemplate || !selectedCount
                              }
                              onClick={handleSendMessage}
                            >
                              {sending
                                ? "Sending..."
                                : `Send notification${
                                    selectedCount
                                      ? ` to ${selectedCount} ${entity.many}`
                                      : ""
                                  }`}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {progress.total > 0 && (
                    <div className="col-12 mb-4">
                      <div className="card shadow-sm">
                        <div className="card-body">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h5 className="mb-0">Delivery progress</h5>
                            <span className="text-muted">
                              {completed} of {progress.total}
                            </span>
                          </div>

                          <div className="progress" style={{ height: 8 }}>
                            <div
                              className={`progress-bar ${
                                sending
                                  ? "progress-bar-striped progress-bar-animated"
                                  : ""
                              } bg-success`}
                              style={{
                                width: `${percent}%`,
                              }}
                            />
                          </div>

                          <div className="row text-center mt-3">
                            {[
                              ["Total", progress.total, "text-muted"],
                              ["Pending", progress.pending, "text-primary"],
                              ["Sent", progress.sent, "text-success"],
                              ["Failed", progress.failed, "text-danger"],
                            ].map(([label, value, className]) => (
                              <div className="col" key={label}>
                                <div className={className}>{label}</div>
                                <h4 className="mb-0">{value}</h4>
                              </div>
                            ))}
                          </div>

                          {failures.length > 0 && !sending && (
                            <div className="mt-3 d-flex align-items-center justify-content-between border-top pt-3">
                              <span className="text-danger">
                                {failures.length} numbers did not go through.
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={downloadFailures}
                              >
                                Download failed list
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
               STUDENT / STAFF SELECTION MODAL
            ========================================================= */}
      {showStudentModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ background: "rgba(0,0,0,.55)", zIndex: 2000 }}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">
                    Select / Edit {entity.Many}
                  </h5>
                  <div className="small text-muted">
                    {isStaffType
                      ? "Choose the staff who should receive the message. Only staff with a valid mobile number are listed."
                      : "Filter by Bus, Hostel, fee status and RTE, then send only to selected students."}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowStudentModal(false)}
                />
              </div>

              <div className="modal-body">
                {/* STUDENT TYPE */}
                {isBatchType && (
                  <div className="border rounded-3 p-3 mb-3 bg-light">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <div>
                        <div className="fw-bold">
                          <i className="ri-filter-3-line me-2 text-primary" />
                          Student Type
                        </div>
                        <small className="text-muted">
                          Select Bus, Hostel, Bus + Hostel or Day Scholar
                          students.
                        </small>
                      </div>
                      <span className="badge bg-primary px-3 py-2">
                        {modalFilteredRecipients.length} matching
                      </span>
                    </div>

                    <div className="row g-2">
                      {[
                        [
                          "all",
                          "All Students",
                          "ri-group-line",
                          recipients.length,
                        ],
                        [
                          "bus",
                          "Bus Students",
                          "ri-bus-line",
                          busStudentsCount,
                        ],
                        [
                          "hostel",
                          "Hostel Students",
                          "ri-community-line",
                          hostelStudentsCount,
                        ],
                        [
                          "bus_hostel",
                          "Bus + Hostel",
                          "ri-building-2-line",
                          busHostelStudentsCount,
                        ],
                        [
                          "day_scholar",
                          "Day Scholars",
                          "ri-user-line",
                          dayScholarStudentsCount,
                        ],
                      ].map(([value, label, icon, count]) => (
                        <div className="col-6 col-md-4 col-xl" key={value}>
                          <button
                            type="button"
                            className={`btn w-100 h-100 text-start p-3 ${
                              studentTypeFilter === value
                                ? "btn-primary"
                                : "btn-outline-primary"
                            }`}
                            onClick={() => applyStudentTypeFilter(value)}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <i className={`${icon} fs-4`} />
                              <span
                                className={`badge ${
                                  studentTypeFilter === value
                                    ? "bg-white text-primary"
                                    : "bg-label-primary"
                                }`}
                              >
                                {count}
                              </span>
                            </div>
                            <div className="fw-semibold mt-2">{label}</div>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* SEARCH */}
                <div className="input-group mb-3">
                  <span className="input-group-text">
                    <i className="ri-search-line" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={
                      isStaffType
                        ? "Search staff name, registration, mobile, email..."
                        : "Search student, registration, mobile, batch..."
                    }
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                  />
                </div>

                {/* FEE FILTER */}
                {isBatchType && (
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <span className="small fw-semibold text-muted me-1">
                      Fees:
                    </span>

                    {[
                      ["all", `All Fees (${recipients.length})`],
                      ["due", `Due Fees (${dueStudentsCount})`],
                      ["paid", `Paid (${paidStudentsCount})`],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={`btn btn-sm ${
                          studentFeeFilter === value
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => applyFeeFilter(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {/* RTE FILTER */}
                {isBatchType && (
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                    <span className="small fw-semibold text-muted me-1">
                      RTE:
                    </span>

                    {[
                      ["all", `All Students (${recipients.length})`],
                      ["rte", `RTE (${rteStudentsCount})`],
                      ["non_rte", `Non-RTE (${nonRteStudentsCount})`],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        className={`btn btn-sm ${
                          studentRteFilter === value
                            ? "btn-primary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() => applyRteFilter(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                {/* SELECTION BAR */}
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <div>
                    <span className="fw-semibold">
                      {selectedCount} selected
                    </span>
                    <span className="small text-muted ms-2">
                      / {recipients.length} {entity.many}
                    </span>
                    <span className="small text-primary ms-2">
                      ({modalFilteredRecipients.length} visible)
                    </span>
                  </div>

                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={selectVisibleRecipients}
                      disabled={!modalFilteredRecipients.length}
                    >
                      <i className="ri-checkbox-line me-1" />
                      Select Only Visible
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={addVisibleRecipients}
                      disabled={!modalFilteredRecipients.length}
                    >
                      Add Visible
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-warning"
                      onClick={unselectVisibleRecipients}
                      disabled={!modalFilteredRecipients.length}
                    >
                      Unselect Visible
                    </button>

                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={clearRecipientSelection}
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="alert alert-primary py-2 mb-3">
                  <strong>{selectedCount}</strong> {entity.one}(s) will receive
                  the WhatsApp message.
                  {isBatchType && (
                    <span className="ms-1">
                      Active filters:{" "}
                      <strong>
                        {studentTypeFilter === "all"
                          ? "All Students"
                          : studentTypeFilter === "bus"
                            ? "Bus Students"
                            : studentTypeFilter === "hostel"
                              ? "Hostel Students"
                              : studentTypeFilter === "bus_hostel"
                                ? "Bus + Hostel"
                                : "Day Scholars"}
                      </strong>
                      {" · "}
                      <strong>
                        {studentFeeFilter === "all"
                          ? "All Fees"
                          : studentFeeFilter === "due"
                            ? "Due Fees"
                            : "Paid"}
                      </strong>
                      {" · "}
                      <strong>
                        {studentRteFilter === "all"
                          ? "All RTE"
                          : studentRteFilter === "rte"
                            ? "RTE"
                            : "Non-RTE"}
                      </strong>
                    </span>
                  )}
                </div>

                {/* STUDENT / STAFF LIST */}
                <div className="row g-2">
                  {modalFilteredRecipients.map(({ recipient, index }) => {
                    const selected = isRecipientSelected(index);

                    /* ---------------- STAFF ROW ---------------- */
                    if (isStaffType) {
                      return (
                        <div
                          className="col-12"
                          key={recipientKey(recipient, index)}
                        >
                          <div
                            className={`border rounded-3 p-3 ${
                              selected
                                ? "border-primary bg-primary-subtle"
                                : "bg-white"
                            }`}
                          >
                            <div className="d-flex align-items-center gap-3">
                              <input
                                type="checkbox"
                                className="form-check-input mt-0"
                                checked={selected}
                                onChange={() => toggleRecipient(index)}
                              />

                              <div
                                className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 38, height: 38 }}
                              >
                                {String(recipient?.name || "S")
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>

                              <div className="flex-grow-1 min-w-0">
                                <div className="d-flex flex-wrap align-items-center gap-2">
                                  <span className="fw-semibold text-primary">
                                    {recipient?.name || "Staff member"}
                                  </span>

                                  {recipient?.staff_type && (
                                    <span className="badge bg-label-info">
                                      {recipient.staff_type}
                                    </span>
                                  )}

                                  {recipient?.role_name && (
                                    <span className="badge bg-label-secondary">
                                      {recipient.role_name}
                                    </span>
                                  )}

                                  {String(recipient?.status) === "0" && (
                                    <span className="badge bg-label-danger">
                                      Inactive
                                    </span>
                                  )}
                                </div>

                                <div className="small text-muted mt-1">
                                  {recipient?.registration_no || "No ID"}
                                  {" • "}
                                  {recipient?.mobile || "No mobile"}
                                  {recipient?.email
                                    ? ` • ${recipient.email}`
                                    : ""}
                                </div>
                              </div>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  setPreviewRecipientIndex(index);
                                  setShowPreviewModal(true);
                                }}
                              >
                                <i className="ri-eye-line me-1" />
                                Preview
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    /* ---------------- STUDENT ROW ---------------- */
                    const bus = isBusStudent(recipient);
                    const hostel = isHostelStudent(recipient);
                    const batchDue = numberValue(recipient?.batch_total_due);
                    const batchPaid = numberValue(recipient?.batch_total_paid);
                    const batchTotal = numberValue(recipient?.batch_total_fee);
                    const busDue = numberValue(recipient?.bus_due);
                    const hostelDue = numberValue(recipient?.hostel_due);
                    const totalDue = getDueAmount(recipient);

                    return (
                      <div
                        className="col-12"
                        key={recipientKey(recipient, index)}
                      >
                        <div
                          className={`border rounded-3 p-3 ${
                            selected
                              ? "border-primary bg-primary-subtle"
                              : "bg-white"
                          }`}
                        >
                          <div className="d-flex align-items-start gap-3">
                            <input
                              type="checkbox"
                              className="form-check-input mt-1"
                              checked={selected}
                              onChange={() => toggleRecipient(index)}
                            />

                            <div
                              className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center flex-shrink-0"
                              style={{ width: 38, height: 38 }}
                            >
                              {String(
                                recipient?.name ??
                                  recipient?.student_name ??
                                  "S",
                              )
                                .charAt(0)
                                .toUpperCase()}
                            </div>

                            <div className="flex-grow-1 min-w-0">
                              <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                                <div>
                                  <div className="d-flex flex-wrap align-items-center gap-2">
                                    <span className="fw-semibold text-primary">
                                      {recipient?.name ??
                                        recipient?.student_name ??
                                        "Student"}
                                    </span>

                                    {bus && (
                                      <span className="badge bg-label-primary">
                                        <i className="ri-bus-line me-1" />
                                        Bus
                                      </span>
                                    )}

                                    {hostel && (
                                      <span className="badge bg-label-warning">
                                        <i className="ri-community-line me-1" />
                                        Hostel
                                      </span>
                                    )}

                                    {!bus && !hostel && isBatchType && (
                                      <span className="badge bg-label-secondary">
                                        Day Scholar
                                      </span>
                                    )}

                                    {recipient?.rte_status && (
                                      <span
                                        className={`badge ${
                                          String(
                                            recipient.rte_status,
                                          ).toUpperCase() === "RTE"
                                            ? "bg-label-success"
                                            : "bg-label-secondary"
                                        }`}
                                      >
                                        {recipient.rte_status}
                                      </span>
                                    )}
                                  </div>

                                  <div className="small text-muted mt-1">
                                    {recipient?.registration_no ??
                                      recipient?.enquiry_no ??
                                      "No registration"}
                                    {" • "}
                                    {recipient?.batch || "No batch"}
                                    {" • "}
                                    {recipient?.mobile ??
                                      recipient?.phone ??
                                      "No mobile"}
                                  </div>
                                </div>

                                <div className="d-flex align-items-center gap-2">
                                  <span
                                    className={`badge ${getFeeStatusClass(recipient)}`}
                                  >
                                    {totalDue > 0
                                      ? `Total Due ₹${money(totalDue)}`
                                      : "Paid"}
                                  </span>

                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => {
                                      setPreviewRecipientIndex(index);
                                      setShowPreviewModal(true);
                                    }}
                                  >
                                    <i className="ri-eye-line me-1" />
                                    Preview
                                  </button>
                                </div>
                              </div>

                              {isBatchType && (
                                <div className="row g-2 mt-2">
                                  <div className="col-md-4">
                                    <div className="border rounded-2 p-2 h-100 bg-white">
                                      <div className="small fw-semibold">
                                        Batch Fee
                                      </div>
                                      <div className="small text-muted mt-1">
                                        Total ₹{money(batchTotal)}
                                        {" · "}
                                        Paid ₹{money(batchPaid)}
                                      </div>
                                      <div
                                        className={`small fw-semibold mt-1 ${
                                          batchDue > 0
                                            ? "text-danger"
                                            : "text-success"
                                        }`}
                                      >
                                        Due ₹{money(batchDue)}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="col-md-4">
                                    <div className="border rounded-2 p-2 h-100 bg-white">
                                      <div className="small fw-semibold">
                                        Bus Fee
                                      </div>
                                      {bus ? (
                                        <>
                                          <div className="small text-muted mt-1">
                                            Total ₹
                                            {money(recipient?.bus_final_fee)}
                                            {" · "}
                                            Paid ₹{money(recipient?.bus_paid)}
                                          </div>
                                          <div
                                            className={`small fw-semibold mt-1 ${
                                              busDue > 0
                                                ? "text-danger"
                                                : "text-success"
                                            }`}
                                          >
                                            Due ₹{money(busDue)}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="small text-muted mt-1">
                                          Not using bus
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="col-md-4">
                                    <div className="border rounded-2 p-2 h-100 bg-white">
                                      <div className="small fw-semibold">
                                        Hostel Fee
                                      </div>
                                      {hostel ? (
                                        <>
                                          <div className="small text-muted mt-1">
                                            Total ₹
                                            {money(recipient?.hostel_final_fee)}
                                            {" · "}
                                            Paid ₹
                                            {money(recipient?.hostel_paid)}
                                          </div>
                                          <div
                                            className={`small fw-semibold mt-1 ${
                                              hostelDue > 0
                                                ? "text-danger"
                                                : "text-success"
                                            }`}
                                          >
                                            Due ₹{money(hostelDue)}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="small text-muted mt-1">
                                          Not using hostel
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {!isBatchType && (
                                <div className="d-flex flex-wrap gap-2 mt-2">
                                  {recipient?.course && (
                                    <span className="badge bg-label-secondary">
                                      {recipient.course}
                                    </span>
                                  )}
                                  {recipient?.source && (
                                    <span className="badge bg-label-secondary">
                                      {recipient.source}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {!modalFilteredRecipients.length && (
                    <div className="col-12">
                      <div className="text-center py-5 text-muted border rounded-3">
                        <i className="ri-search-eye-line fs-1 d-block mb-2" />
                        <div className="fw-semibold">
                          No {entity.many} match these filters
                        </div>
                        <small>
                          {isStaffType
                            ? "Change your search."
                            : "Change Student Type, Fees, RTE or search."}
                        </small>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer d-flex justify-content-between">
                <div className="small text-muted">
                  {selectedCount} selected of {recipients.length}
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={clearRecipientSelection}
                  >
                    Unselect All
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowStudentModal(false)}
                  >
                    <i className="ri-check-line me-1" />
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
               VARIABLE SELECTION MODAL
            ========================================================= */}
      {showVariableModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ background: "rgba(0,0,0,.55)", zIndex: 2100 }}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">Select Message Variables</h5>
                  <div className="small text-muted">
                    Map each template variable to a {entity.short} field or use
                    a fixed value.
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowVariableModal(false)}
                />
              </div>

              <div className="modal-body">
                {needsHeaderFile && (
                  <div className="mb-4 rounded-3 border p-3">
                    <div className="fw-semibold mb-1">Template media</div>
                    <div className="small text-muted mb-2">
                      This template needs a {String(headerFormat).toLowerCase()}{" "}
                      file.
                    </div>
                    <input
                      type="file"
                      className="form-control"
                      accept={
                        headerFormat === "IMAGE"
                          ? "image/*"
                          : headerFormat === "VIDEO"
                            ? "video/*"
                            : ".pdf,.doc,.docx"
                      }
                      onChange={(e) =>
                        setHeaderFile(e.target.files?.[0] ?? null)
                      }
                    />
                  </div>
                )}

                {headerVariables.length > 0 && (
                  <div className="mb-4">
                    <div className="fw-semibold mb-2">Header variables</div>
                    <div className="row">
                      {headerVariables.map((variable, index) => (
                        <VariableInput
                          key={`modal-header-${index}`}
                          label={`Header variable ${index + 1}`}
                          variable={variable}
                          index={index}
                          setter={setHeaderVariables}
                          dynamicFields={dynamicFields}
                          updateVariable={updateVariable}
                          formatDynamicLabel={formatDynamicLabel}
                          entityLabel={entity.short}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {bodyVariables.length > 0 && (
                  <div className="mb-4">
                    <div className="fw-semibold mb-2">Message variables</div>
                    <div className="row">
                      {bodyVariables.map((variable, index) => (
                        <VariableInput
                          key={`modal-body-${index}`}
                          label={`Message variable ${index + 1}`}
                          variable={variable}
                          index={index}
                          setter={setBodyVariables}
                          dynamicFields={dynamicFields}
                          updateVariable={updateVariable}
                          formatDynamicLabel={formatDynamicLabel}
                          entityLabel={entity.short}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {buttonVariables.length > 0 && (
                  <div className="mb-2">
                    <div className="fw-semibold mb-2">Button variables</div>
                    <div className="row">
                      {buttonVariables.map((variable, index) => (
                        <VariableInput
                          key={`modal-button-${index}`}
                          label={variable.buttonText || `Button ${index + 1}`}
                          variable={variable}
                          index={index}
                          setter={setButtonVariables}
                          dynamicFields={dynamicFields}
                          updateVariable={updateVariable}
                          formatDynamicLabel={formatDynamicLabel}
                          entityLabel={entity.short}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {headerVariables.length === 0 &&
                  bodyVariables.length === 0 &&
                  buttonVariables.length === 0 && (
                    <div className="text-center py-5 text-muted">
                      This template has no variables.
                    </div>
                  )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowVariableModal(false)}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
               PREVIEW ALL MESSAGES MODAL
            ========================================================= */}
      {showPreviewModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ background: "rgba(0,0,0,.55)", zIndex: 2200 }}
        >
          <div
            className="modal-dialog modal-xl modal-dialog-scrollable"
            role="document"
          >
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title mb-1">
                    {entity.One} Message Preview
                  </h5>
                  <div className="small text-muted">
                    Preview exactly what each selected {entity.one} will
                    receive.
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="badge bg-primary">
                    {selectedCount} messages
                  </span>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowPreviewModal(false)}
                  />
                </div>
              </div>

              <div className="modal-body">
                {!selectedCount ? (
                  <div className="text-center py-5 text-muted">
                    Select at least one {entity.one} first.
                  </div>
                ) : (
                  <div className="row g-3">
                    {selectedRecipients.map((recipient, index) => {
                      const message = buildStudentPreview(recipient);
                      const isCurrent =
                        recipientKey(recipient, index) ===
                        recipientKey(previewRecipient, previewRecipientIndex);

                      return (
                        <div
                          className="col-xl-6"
                          key={recipientKey(recipient, index)}
                        >
                          <div
                            className={`border rounded-3 overflow-hidden ${
                              isCurrent ? "border-primary shadow-sm" : ""
                            }`}
                          >
                            <div className="p-3 border-bottom bg-light">
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <div>
                                  <div className="fw-semibold">
                                    {recipient.name ??
                                      recipient.student_name ??
                                      entity.One}
                                  </div>
                                  <div className="small text-muted">
                                    {recipient.mobile ??
                                      recipient.phone ??
                                      "No mobile"}
                                    {" · "}
                                    {recipient.registration_no ??
                                      recipient.enquiry_no ??
                                      "-"}
                                  </div>
                                </div>
                                {isStaffType ? (
                                  recipient.staff_type && (
                                    <span className="badge bg-label-info">
                                      {recipient.staff_type}
                                    </span>
                                  )
                                ) : (
                                  <span
                                    className={`badge ${getFeeStatusClass(recipient)}`}
                                  >
                                    {getFeeStatusLabel(recipient)}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div
                              className="p-3"
                              style={{
                                background: "#efeae2",
                              }}
                            >
                              <div
                                className="bg-white rounded-3 p-3 shadow-sm"
                                style={{
                                  whiteSpace: "pre-wrap",
                                  wordBreak: "break-word",
                                }}
                              >
                                {headerPreview &&
                                  headerFile?.type?.startsWith("image/") && (
                                    <img
                                      src={headerPreview}
                                      alt=""
                                      className="rounded-2 mb-2 d-block"
                                      style={{
                                        width: "100%",
                                        maxHeight: 180,
                                        objectFit: "contain",
                                      }}
                                    />
                                  )}

                                {message.header && (
                                  <div className="fw-semibold mb-1">
                                    {message.header}
                                  </div>
                                )}

                                <div>
                                  {message.body ||
                                    "Your message preview will appear here."}
                                </div>

                                {message.footer && (
                                  <div className="text-muted mt-2 small">
                                    {message.footer}
                                  </div>
                                )}

                                {message.buttons.length > 0 && (
                                  <div className="border-top mt-2 pt-2">
                                    {message.buttons.map(
                                      (button, buttonIndex) => (
                                        <div
                                          key={buttonIndex}
                                          className="text-center py-1 text-primary border-bottom"
                                        >
                                          {button.text}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowPreviewModal(false)}
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
